/**
 * DEMO MODE — local-only, flag-gated (VITE_DEMO=true).
 *
 * Lets you click through the real Phase-0 UI without Cognito, the API, or a
 * database. It replaces the network `api` layer with an in-memory store and
 * lets the Login screen enter without authenticating. Nothing here runs unless
 * the VITE_DEMO env flag is set, so the production code paths are untouched.
 *
 * This file is intentionally NOT part of Phase 0 — it's a throwaway dev aid.
 */
import type {
  AssistantChatResponse,
  AssistantLogListResponse,
  AssistantLogRecord,
  AssistantExecuteResponse,
  AssistantProposedAction,
  AssistantToolResult,
  AssistantToolInfo,
  AssistantToolsResponse,
  AuditListResponse,
  CreateTenantResponse,
  DocumentDownloadUrlResponse,
  DueRenewalResponse,
  EntityMode,
  ExpedienteActivityEntry,
  ExpedienteActivityResponse,
  ExpedienteCanal,
  ExpedienteProducto,
  ExpedienteResponse,
  ExpedienteVersionResponse,
  ExpedienteVersionsResponse,
  UpdateExpedienteInput,
  MeResponse,
  OcrPrefillResult,
  OutreachChannel,
  OutreachListResponse,
  OutreachMessageResponse,
  OutreachReason,
  RegistryOfficerResponse,
  RoleResponse,
  RunOutreachResponse,
  RunRenewalsResponse,
  ScreeningItemResult,
  TenantResponse,
  TenantSettingsResponse,
  UpdateOutreachInput,
  UserResponse,
  VinculoResponse,
} from '@cumpli/shared';
import {
  ALERT_TYPE_RENOVACION,
  LEY23_EJE_LABELS,
  LEY23_KEY_DEADLINES,
  LEY23_OBLIGATION_REFERENCE,
  LEY23_OBLIGATION_GAPS,
  ley23SujetoAplica,
  RENOVACION_PRIORITY_BY_NIVEL,
  RISK_CADENCE_MONTHS,
  ROLE_SEEDS,
  RECOMMENDED_RISK_MATRIX,
  RISK_COUNTRY_RISK_CATALOG,
  RISK_FACTOR_REFERENCE,
  RISK_INDICATOR_REFERENCE,
  SECTOR_RISK_PRESETS,
  computeExpedienteRisk,
  composeOutreachMessage,
  parseRpDocumento,
  type RiskComputation,
  type Ley23Eje,
  type RiskMatrixConfig,
  type RiskMatrixResponse,
} from '@cumpli/shared';
import { RequestError } from './request-error';
import { auditActionLabel } from './audit-labels';
import {
  SUBSECTOR_LABEL,
  VIGILANCIA_TAB,
  VIGENCIA_LABEL,
  evaluarFolio,
  resolverConsultaDemo,
  type RpConsultaInput,
  type RpConsultaResult,
  type RpWatch,
  type RpWeeklyCheckResult,
  type RpLoginInput,
  type RpSalud,
  type RpSweepResult,
  type RpSweepRow,
} from './registro';
import {
  ALERTS_SEED,
  EXPEDIENTES_SEED,
  parseReviewDate,
  daysUntil,
  todayIso,
} from './phase1';
import type {
  Alert,
  AlertPatch,
  Consent,
  ConsentPatch,
  CreateConsentInput,
  CreateOwnerInput,
  CreateReviewInput,
  CreateSubjectInput,
  EstadoExpediente,
  Expediente,
  ExpedienteDoc,
  ExpedienteDocPatch,
  NewExpedienteInput,
  NivelRiesgo,
  Owner,
  OwnerPatch,
  Persona,
  Peso,
  RiskFactor,
  Review,
  ReviewPatch,
  ReviewType,
  RunValidationBody,
  Subject,
  SubjectPatch,
  ValidationRun,
} from './phase1';

export const DEMO: boolean = import.meta.env.VITE_DEMO === 'true';

// Runtime kill-switch: a VITE_DEMO=true build must never run on a real production
// domain. If it does, the fake in-memory data would be served to real users. Bail
// out loudly before React mounts so nothing renders.
if (DEMO && typeof window !== 'undefined') {
  const host = window.location.hostname;
  const safeHost =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.includes('demo') ||
    host.includes('beta');
  if (!safeHost) {
    document.body.innerHTML =
      '<p style="font-family:sans-serif;padding:2rem;color:#5f1f29">' +
      'Error de configuración: instancia demo activa en dominio de producción. ' +
      'Reconstruya sin <code>VITE_DEMO</code>. Contacte al equipo técnico.</p>';
    throw new Error('[Cumpli] demo build forbidden on production host: ' + host);
  }
}

const delay = <T>(value: T, ms = 240): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

/**
 * The signed-in identity. Given tenant_admin + super_admin + compliance_manager
 * purely as a demo convenience, so every screen (Usuarios + Entidades + the
 * compliance-staff tools like Validación masiva) is reachable. In the real model
 * a super_admin lives in the platform tenant and never holds a tenant role, and
 * tenant_admin does not run validations — see README decision #2.
 */
const me: MeResponse = {
  id: 'u-001',
  email: 'ana.robles@miexito.com.pa',
  fullName: 'Ana Robles',
  tenantId: 't-001',
  tenantName: 'Corporación Bellavista de Finanzas (Mi Éxito)',
  roles: ['tenant_admin', 'super_admin', 'compliance_manager'],
};

const roles: RoleResponse[] = ROLE_SEEDS.filter((r) => !r.isPlatform).map((r) => ({
  key: r.key,
  name: r.name,
}));

let users: UserResponse[] = [
  { id: 'u-001', fullName: 'Ana Robles', email: 'ana.robles@miexito.com.pa', roles: ['tenant_admin'], status: 'active', lastLoginAt: '2026-06-12T08:41:00' },
  { id: 'u-002', fullName: 'Julio Castillo', email: 'julio.castillo@miexito.com.pa', roles: ['compliance_manager'], status: 'active', lastLoginAt: '2026-06-11T17:52:00' },
  { id: 'u-003', fullName: 'María Mendoza', email: 'maria.mendoza@miexito.com.pa', roles: ['analyst'], status: 'active', lastLoginAt: '2026-06-10T14:05:00' },
  { id: 'u-004', fullName: 'Roberto Achong', email: 'roberto.achong@miexito.com.pa', roles: ['intake_officer'], status: 'active', lastLoginAt: null },
  { id: 'u-005', fullName: 'Lucía Fernández', email: 'lucia.fernandez@miexito.com.pa', roles: ['auditor'], status: 'active', lastLoginAt: '2026-06-09T09:13:00' },
  { id: 'u-006', fullName: 'Carlos Him', email: 'carlos.him@miexito.com.pa', roles: ['analyst'], status: 'deactivated', lastLoginAt: '2026-03-02T11:00:00' },
];

let tenants: TenantResponse[] = [
  { id: 't-001', legalName: 'Corporación Bellavista de Finanzas, S.A.', status: 'active', planTier: 'pilot', createdAt: '2026-02-20T10:00:00', userCount: 6 },
  { id: 't-platform', legalName: 'Cumpli (Plataforma)', status: 'active', planTier: 'internal', createdAt: '2026-01-05T10:00:00', userCount: 2 },
  { id: 't-002', legalName: 'Despacho Legal Arosemena & Asoc., S.A.', status: 'offboarding', planTier: 'standard', createdAt: '2025-11-11T10:00:00', userCount: 4 },
];

let seq = 7;
const nextId = (prefix: string): string => `${prefix}-${String(seq++).padStart(3, '0')}`;

// ---- Phase-1 preview store (expedientes + alerts) ----
let expedientes: Expediente[] = EXPEDIENTES_SEED.map((e) => ({ ...e }));
let alerts: Alert[] = ALERTS_SEED.map((a) => ({ ...a }));
// Above the highest seed id (max EXP-2026-0053) so a newly-registered client can
// never collide with a seeded expediente. EXP-2026-0042 stays a valid seed id.
let expSeq = 54;

// Papelera (soft-delete) store. Rows are kept in the raw ExpedienteResponse shape
// (plus `deletedAt`) to mirror the real GET ?deleted=true response.
type DemoPapeleraRow = ExpedienteResponse & { deletedAt: string | null };
let papelera: DemoPapeleraRow[] = [];

// Convert a UI Expediente <-> the raw response shape used by the papelera list.
const toPapeleraRow = (e: Expediente, deletedAt: string): DemoPapeleraRow => ({
  id: e.id,
  ref: e.id,
  persona: e.persona,
  nombre: e.name,
  docId: e.docId,
  sector: e.sector,
  kind: e.kind,
  email: e.email,
  phone: e.phone,
  estado: e.estado,
  riesgo: e.riesgo,
  score: e.score,
  pep: e.pep,
  assignedTo: null,
  nextReviewAt: e.review,
  canal: e.canal ?? null,
  producto: e.producto ?? null,
  docKind: e.docKind ?? null,
  folio: e.folio ?? null,
  constitutionDate: e.constitutionDate ?? null,
  dateOfBirth: e.dateOfBirth ?? null,
  direccion: e.direccion ?? null,
  startedAt: e.started,
  createdAt: e.started,
  updatedAt: deletedAt,
  deletedAt,
});

const fromPapeleraRow = (r: DemoPapeleraRow): Expediente => ({
  id: r.id,
  persona: r.persona,
  name: r.nombre,
  kind: r.kind,
  icon: r.persona === 'legal' ? '🏛️' : '🧑',
  docId: r.docId,
  sector: r.sector,
  email: r.email,
  phone: r.phone,
  dateOfBirth: r.dateOfBirth ?? null,
  direccion: r.direccion ?? null,
  canal: r.canal as ExpedienteCanal | null,
  producto: r.producto as ExpedienteProducto | null,
  docKind: r.docKind,
  folio: r.folio,
  constitutionDate: r.constitutionDate,
  started: r.startedAt,
  estado: r.estado as EstadoExpediente,
  riesgo: r.riesgo as Expediente['riesgo'],
  score: r.score,
  pep: r.pep,
  analyst: null,
  review: r.nextReviewAt,
  fields: [],
  docs: [],
  validations: [],
  factors: [],
  ubo: null,
  history: [],
});

// ---- Audit-trail demo seed ----
// A handful of representative append-only events: some carry evidence (an
// attached HTML blob, previewable), others carry before/after JSON states that
// expand inline. Filtered client-side by entityType/action in listAudit.
const AUDIT_SEED: AuditListResponse['items'] = [
  {
    eventKey: '2026-07-12T14:22:10Z#evt-1001',
    eventId: 'evt-1001',
    action: 'expediente.update',
    entityType: 'expediente',
    entityId: 'EXP-2026-0042',
    actorName: 'Ana Robles',
    actorRole: 'compliance_manager',
    createdAt: '2026-07-12T14:22:10Z',
    hasEvidence: false,
    evidenceName: null,
    beforeState: { estado: 'En Revisión', riesgo: 'medio' },
    afterState: { estado: 'Activo', riesgo: 'medio' },
  },
  {
    eventKey: '2026-07-12T13:05:44Z#evt-1000',
    eventId: 'evt-1000',
    action: 'validation.run',
    entityType: 'validation',
    entityId: 'VR-SANCTIONS-0042',
    actorName: 'María Mendoza',
    actorRole: 'analyst',
    createdAt: '2026-07-12T13:05:44Z',
    hasEvidence: true,
    evidenceName: 'Cotejo de listas — OFAC/ONU/UE',
  },
  {
    eventKey: '2026-07-11T09:41:02Z#evt-0999',
    eventId: 'evt-0999',
    action: 'expediente.delete',
    entityType: 'expediente',
    entityId: 'EXP-2026-0031',
    actorName: 'Julio Castillo',
    actorRole: 'compliance_manager',
    createdAt: '2026-07-11T09:41:02Z',
    hasEvidence: false,
    evidenceName: null,
    beforeState: { estado: 'Rechazado' },
    afterState: null,
  },
  {
    eventKey: '2026-07-10T16:18:30Z#evt-0998',
    eventId: 'evt-0998',
    action: 'document.upload',
    entityType: 'document',
    entityId: 'DOC-2026-0187',
    actorName: 'Roberto Achong',
    actorRole: 'intake_officer',
    createdAt: '2026-07-10T16:18:30Z',
    hasEvidence: true,
    evidenceName: 'Comprobante de carga — cédula.pdf',
  },
  {
    eventKey: '2026-07-09T11:02:12Z#evt-0997',
    eventId: 'evt-0997',
    action: 'user.deactivate',
    entityType: 'user',
    entityId: 'u-006',
    actorName: 'Ana Robles',
    actorRole: 'tenant_admin',
    createdAt: '2026-07-09T11:02:12Z',
    hasEvidence: false,
    evidenceName: null,
    beforeState: { status: 'active' },
    afterState: { status: 'deactivated' },
  },
];

// Live audit trail: the append-only log the demo actually serves. Seeded from
// AUDIT_SEED, then every demo write (create/update/delete/run-validation/upload)
// prepends a fresh event via `pushAudit`, so Auditoría shows the walkthrough's
// own actions — not a frozen list.
let auditSeq = 1002;
let auditLog: AuditListResponse['items'] = AUDIT_SEED.map((e) => ({ ...e }));

function pushAudit(entry: {
  action: string;
  entityType: string;
  entityId: string;
  actorRole?: string;
  hasEvidence?: boolean;
  evidenceName?: string | null;
  beforeState?: unknown;
  afterState?: unknown;
}): void {
  const createdAt = nowIso();
  const eventId = `evt-${auditSeq++}`;
  const row: AuditListResponse['items'][number] = {
    eventKey: `${createdAt}#${eventId}`,
    eventId,
    action: entry.action,
    entityType: entry.entityType,
    entityId: entry.entityId,
    actorName: me.fullName,
    actorRole: entry.actorRole ?? 'compliance_manager',
    createdAt,
    hasEvidence: entry.hasEvidence ?? false,
    evidenceName: entry.evidenceName ?? null,
    ...(entry.beforeState !== undefined ? { beforeState: entry.beforeState } : {}),
    ...(entry.afterState !== undefined ? { afterState: entry.afterState } : {}),
  };
  auditLog = [row, ...auditLog];
}

/**
 * Build a CSP-safe data URI for the file-preview dialog. The app CSP allows
 * `img-src data:` but NOT `frame-src data:`, so evidence/documents are rendered
 * as an on-brand SVG "page" (shown via <img>) rather than a framed PDF/HTML.
 * btoa can't take non-ASCII directly, so UTF-8 the string first (accents).
 */
function svgDataUri(heading: string, lines: string[]): DocumentDownloadUrlResponse {
  const esc = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const body = lines
    .map((l, i) => `<text x="48" y="${150 + i * 34}" font-size="17" fill="#3f3a39">${esc(l)}</text>`)
    .join('');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="720" height="560" viewBox="0 0 720 560">` +
    `<rect width="720" height="560" fill="#f8f5f4"/>` +
    `<rect x="24" y="24" width="672" height="512" rx="18" fill="#ffffff" stroke="#e7e0de"/>` +
    `<rect x="24" y="24" width="672" height="64" rx="18" fill="#8a2e3c"/>` +
    `<text x="48" y="64" font-size="22" font-family="Georgia, serif" fill="#ffffff">Cumpli — ${esc(heading)}</text>` +
    `<text x="48" y="118" font-size="13" fill="#8a2e3c" font-family="monospace">EVIDENCIA / DOCUMENTO (demostración)</text>` +
    body +
    `<text x="48" y="512" font-size="12" fill="#a49f9d">Documento sintético para la demostración — datos ficticios.</text>` +
    `</svg>`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return {
    url: `data:image/svg+xml;base64,${b64}`,
    mimeType: 'image/svg+xml',
    fileName: 'evidencia.svg',
    expiresInSeconds: 60,
  };
}

/**
 * Minimal single-page PDF built by hand (no library, no network — I6-safe,
 * demo-only) so the "Descargar PDF" button downloads a real, openable PDF
 * instead of crashing or silently mislabeling a CSV. Not a layout engine:
 * plain monospaced text lines, truncated to fit one page.
 */
function buildSimplePdf(title: string, lines: string[]): Blob {
  const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
  const maxLines = 46;
  const body = [title, '', ...lines].slice(0, maxLines);
  const textOps = body
    .map((line, i) => `BT /F1 10 Tf 40 ${780 - i * 16} Td (${esc(line)}) Tj ET`)
    .join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    `<< /Length ${textOps.length} >>\nstream\n${textOps}\nendstream`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((obj, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const off of offsets) pdf += `${String(off).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
  return new Blob([pdf], { type: 'application/pdf' });
}

// Vigilancia Registral: folios vigilados + dedupe de alertas ya emitidas.
let rpWatchlist: RpWatch[] = [];
let rpWatchSeq = 1;
let rpAlertSeq = 1;
const rpEmitted = new Set<string>(); // clave folio:subsector para no duplicar alertas

// §3 Renovación de debida diligencia: dedupe de alarmas ya abiertas (por
// expediente + fecha de renovación, como el patrón rp) e id secuencial.
let renewalAlertSeq = 1;
const renewalEmitted = new Set<string>(); // clave expedienteId:nextReviewAt

// Rich Phase-1 in-memory stores (subjects/owners/reviews/consents/documents),
// keyed by expedienteId. Empty by default — seeded lazily on first write so the
// demo screens are navigable without a backend.
// Fase 4 drill-down (req#7): the demo doc carries an optional `ownerId` (null =
// entity-level) so the persona filter + entity-view label work in demo mode too.
type DemoDoc = ExpedienteDoc & { ownerId?: string | null };
const docsStore: Record<string, DemoDoc[]> = {};
// Soft-deleted (papelera) documents, keyed by expedienteId — mirrors the real
// GET .../documents?deleted=true set.
const deletedDocsStore: Record<string, DemoDoc[]> = {};
const subjectsStore: Record<string, Subject[]> = {};
const ownersStore: Record<string, Owner[]> = {};
const reviewsStore: Record<string, Review[]> = {};
const consentsStore: Record<string, Consent[]> = {};
let richSeq = 1;
const richId = (prefix: string): string => `${prefix}-${String(richSeq++).padStart(4, '0')}`;
const nowIso = (): string => new Date().toISOString();

/** Lazily seed `docsStore[expedienteId]` from the expediente's inline `docs`
 *  the first time anything reads it (GET-triggered, idempotent) — shared by
 *  `listDocuments` and the activity-log synthesis so both see the same rows. */
function ensureDocsSeeded(expedienteId: string): void {
  if (docsStore[expedienteId]) return;
  const e = expedientes.find((x) => x.id === expedienteId);
  docsStore[expedienteId] = (e?.docs ?? []).map((d) => ({
    id: richId('doc'),
    expedienteId,
    category: d.category,
    name: d.name,
    estado: d.estado,
    expiry: d.expiry,
    documentTypeId: null,
    ownerId: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }));
}

/** Lazily seed ONE recoverable (soft-deleted) document for `expedienteId` so
 *  the redesigned "Elementos eliminados recuperables" surface has something
 *  real to restore in demo mode — idempotent (a no-op once seeded), and
 *  additive: it never removes anything from the active `docsStore` list. */
function ensureDeletedDocSeed(expedienteId: string): DemoDoc | null {
  if (deletedDocsStore[expedienteId] && deletedDocsStore[expedienteId].length > 0) {
    return deletedDocsStore[expedienteId][0] ?? null;
  }
  ensureDocsSeeded(expedienteId);
  const base = docsStore[expedienteId]?.[0];
  if (!base) return null;
  const doc: DemoDoc = {
    id: richId('doc'),
    expedienteId,
    category: base.category,
    name: `${base.name} (versión anterior)`,
    estado: 'Vencido',
    expiry: null,
    documentTypeId: null,
    ownerId: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  deletedDocsStore[expedienteId] = [doc];
  return doc;
}

/**
 * Seed the rich detail-tab stores (subjects / owners / reviews / consents) from
 * EXPEDIENTES_SEED so the four tabs on ExpedienteDetail render substance in demo
 * mode without a backend. Runs once at module load. Each legal client's UBO
 * nodes become Owner rows (ownership % parsed from the node role); every
 * expediente gets a subject row + a scheduled periodic review derived from its
 * risk cadence; natural persons and legal clients get an APC/registro consent.
 */
function seedRichStores(): void {
  const isoStamp = '2026-06-10T09:00:00.000Z';
  const LEGAL_HINT =
    /\b(s\.?a\.?|ltd|holdings?|grupo|bufete|junta|autocartera|tesorer|cooperativa|inc|corp|compa[ñn][ií]a|beneficiarios)\b/i;
  const parsePct = (role: string): number => {
    const m = role.match(/(\d{1,3})\s*%/);
    return m ? Number(m[1]) : 0;
  };
  const ownerCountry = (name: string, detail: string): string => {
    const hay = `${name} ${detail}`;
    if (/bvi|islas v[ií]rgenes/i.test(hay)) return 'Islas Vírgenes Británicas (BVI)';
    if (/hong\s*kong/i.test(hay)) return 'Hong Kong';
    return 'Panamá';
  };
  const ownerKyc = (detail: string): boolean =>
    /\b(kyc completo|verificad|completo|identificad)\b/i.test(detail) &&
    !/\b(pendiente|en curso|en verificaci|verificaci[oó]n)\b/i.test(detail);

  for (const e of EXPEDIENTES_SEED) {
    // --- Subject (the client/entity under diligence) ---
    subjectsStore[e.id] = [
      {
        id: richId('sub'),
        expedienteId: e.id,
        subjectRef: e.name,
        subjectType: e.persona,
        declaredAddress: e.persona === 'legal' ? 'Ciudad de Panamá, República de Panamá' : 'Provincia de Panamá',
        sector: e.sector,
        activity: e.kind,
        requiredDocuments: [...new Set(e.docs.map((d) => d.category))],
        createdAt: isoStamp,
        updatedAt: isoStamp,
      },
    ];

    // --- Owners (UBO graph) — legal clients only ---
    if (e.persona === 'legal' && e.ubo && e.ubo.length > 0) {
      ownersStore[e.id] = e.ubo.map((node) => {
        const ownerType: Persona = LEGAL_HINT.test(node.name) ? 'legal' : 'natural';
        return {
          id: richId('own'),
          expedienteId: e.id,
          subjectRef: e.name,
          parentOwnerId: null,
          name: node.name,
          ownerType,
          ownershipPct: parsePct(node.role),
          idNumber: null,
          country: ownerCountry(node.name, node.detail),
          kycComplete: ownerKyc(node.detail),
          kycExpedienteId: null,
          createdAt: isoStamp,
          updatedAt: isoStamp,
        };
      });
    }

    // --- Scheduled review (periodic re-review by risk cadence) ---
    if (e.riesgo !== 'sin') {
      const scheduledFor = parseReviewDate(e.review) ?? isoStamp;
      reviewsStore[e.id] = [
        {
          id: richId('rev'),
          expedienteId: e.id,
          reviewType: 'periodica',
          riskLevel: e.riesgo,
          cadenceMonths: RISK_CADENCE_MONTHS[e.riesgo],
          scheduledFor,
          status: 'pendiente',
          assignedTo: e.analyst,
          completedAt: null,
          createdAt: isoStamp,
          updatedAt: isoStamp,
        },
      ];
    }

    // --- Consent (APC/registro consult authorization) ---
    consentsStore[e.id] = [
      {
        id: richId('con'),
        expedienteId: e.id,
        subjectRef: e.name,
        subjectType: e.persona,
        source: e.persona === 'legal' ? 'Consulta a Registro Público (autorizada)' : 'APC — autorización de consulta de crédito',
        evidenceRef: `Autorización firmada · ${e.docId}`,
        grantedAt: `${e.started}T09:00:00.000Z`,
        expiresAt: null,
        revokedAt: null,
        recordedBy: e.analyst ?? me.fullName,
        createdAt: `${e.started}T09:00:00.000Z`,
      },
    ];
  }
}
seedRichStores();

// ---- "Alcance a clientes" (outreach) demo store ----
// Seeded with plausible es-PA messages covering every status so the page is
// fully demoable without ever calling `generar`. `composeOutreachMessage` (the
// same pure composer the backend uses) keeps subject/body realistic.
let outreachSeq = 7;
const nextOutreachId = (): string => `ALC-${String(outreachSeq++).padStart(4, '0')}`;

function buildOutreachMessage(input: {
  expedienteId: string;
  clientName: string;
  reason: OutreachReason;
  documentNames: string[];
  channels: OutreachChannel[];
}): OutreachMessageResponse {
  const composed = composeOutreachMessage({
    reason: input.reason,
    clientName: input.clientName,
    tenantName: me.tenantName,
    expedienteRef: input.expedienteId,
    documentNames: input.documentNames,
  });
  return {
    id: nextOutreachId(),
    expedienteId: input.expedienteId,
    expedienteRef: input.expedienteId,
    clientName: input.clientName,
    channels: input.channels,
    reason: input.reason,
    subject: composed.subject,
    body: composed.body,
    status: 'draft',
    scheduledAt: null,
    sentAt: null,
    error: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
}

let outreachMessages: OutreachMessageResponse[] = [
  buildOutreachMessage({
    expedienteId: 'EXP-2026-0039',
    clientName: 'Ana Lucía Moreno Beitía',
    reason: 'documento_por_vencer',
    documentNames: ['Cédula (frente/dorso)'],
    channels: ['email'],
  }),
  buildOutreachMessage({
    expedienteId: 'EXP-2026-0036',
    clientName: 'Inmobiliaria Costa Pacífico Development, S.A.',
    reason: 'documento_por_vencer',
    documentNames: ['Certificado de Registro Público'],
    channels: ['whatsapp', 'email'],
  }),
  {
    ...buildOutreachMessage({
      expedienteId: 'EXP-2026-0053',
      clientName: 'Importadora y Distribuidora Global Trade Zone, S.A.',
      reason: 'documento_solicitado',
      documentNames: ['Certificado de vigencia del holding (BVI)'],
      channels: ['whatsapp'],
    }),
    status: 'scheduled',
    scheduledAt: new Date(Date.now() + 2 * 24 * 3600 * 1000).toISOString(),
  },
  {
    ...buildOutreachMessage({
      expedienteId: 'EXP-2026-0039',
      clientName: 'Ana Lucía Moreno Beitía',
      reason: 'documento_vencido',
      documentNames: ['Declaración de renta (DGI)'],
      channels: ['whatsapp', 'email'],
    }),
    status: 'sent',
    sentAt: '2026-06-25T09:15:00.000Z',
  },
  {
    ...buildOutreachMessage({
      expedienteId: 'EXP-2026-0042',
      clientName: 'Casa de Cambio y Remesas Centroamérica, S.A.',
      reason: 'documento_solicitado',
      documentNames: ['Programa de cumplimiento actualizado'],
      channels: ['email', 'whatsapp'],
    }),
    status: 'failed',
    error: 'No se pudo entregar el mensaje (número de WhatsApp inválido).',
  },
  {
    ...buildOutreachMessage({
      expedienteId: 'EXP-2026-0053',
      clientName: 'Importadora y Distribuidora Global Trade Zone, S.A.',
      reason: 'documento_vencido',
      documentNames: ['Declaración de beneficiario final del holding'],
      channels: ['whatsapp'],
    }),
    status: 'canceled',
  },
];

// Extra suggestions `generar` adds the first time it runs — idempotent (won't
// duplicate a suggestion already present for the same expediente + motivo).
const OUTREACH_CANDIDATES: {
  expedienteId: string;
  clientName: string;
  reason: OutreachReason;
  documentNames: string[];
  channels: OutreachChannel[];
}[] = [
  {
    expedienteId: 'EXP-2026-0036',
    clientName: 'Inmobiliaria Costa Pacífico Development, S.A.',
    reason: 'documento_por_vencer',
    documentNames: ['Estados financieros'],
    channels: ['email'],
  },
  {
    expedienteId: 'EXP-2026-0053',
    clientName: 'Importadora y Distribuidora Global Trade Zone, S.A.',
    reason: 'documento_solicitado',
    documentNames: ['Certificado de vigencia del holding (BVI)'],
    channels: ['whatsapp'],
  },
];

// Demo persistence: unlike the rest of this in-memory store, the saved matrix and
// firm mode are written to localStorage so "Guardar matriz" actually sticks across
// a reload (mirrors the real API's per-tenant persistence, and matches the page's
// bandOverrides/enabledIndicators pattern). Safe when storage is unavailable.
const DEMO_RISK_MATRIX_KEY = 'cumpli.demo.riskMatrix.v1';
const DEMO_ENTITY_MODE_KEY = 'cumpli.demo.entityMode.v1';
function loadDemo<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function saveDemo(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* almacenamiento no disponible — sigue en memoria */
  }
}

// Demo risk matrix — starts unsaved (version 0), opening on the documented
// recommended sub-factor split (mirrors the real handler's version-0 response).
// Rehydrated from localStorage so a saved matrix survives a page reload.
let demoRiskMatrix = loadDemo<RiskMatrixResponse>(DEMO_RISK_MATRIX_KEY, {
  version: 0,
  weights: { cliente: 50, jurisdiccion: 10, canal: 10, producto: 30 },
  thresholds: { medio: 30, alto: 60 },
  subweights: RECOMMENDED_RISK_MATRIX.subweights!,
  updatedAt: null as string | null,
});

// Demo firm mode (§2) — defaults to 'financiero' (full screening), matching the
// real handler's default. Switching it re-scopes which risk indicators apply.
let demoEntityMode: EntityMode = loadDemo<EntityMode>(DEMO_ENTITY_MODE_KEY, 'financiero');

const stamp = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `2026-06-12 ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** `stamp()`'s "YYYY-MM-DD HH:mm" -> ISO 8601, for synthesizing version-history
 *  `createdAt` from the plain-text `history` seed. Falls back to "now" if the
 *  seed ever carries an unparsable string. */
function stampToIso(ts: string): string {
  const d = new Date(ts.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? nowIso() : d.toISOString();
}

/**
 * DEMO sanctions watchlist — mirrors the backend StubSanctionsProvider's three
 * hard hits plus one rare-token `review` near-match, so BOTH the per-client
 * validation flow (runValidation) and the batch endpoint (screenBatch) behave
 * like the real screen. Keyed by the normalized name.
 */
const DEMO_WATCHLIST: Record<
  string,
  { listName: string; tier: 'hit' | 'review'; matchedName?: string; score?: number }
> = {
  'osama bin laden': { listName: 'UN Consolidated', tier: 'hit' },
  'juan perez sancionado': { listName: 'OFAC SDN', tier: 'hit' },
  'empresa fantasma sa': { listName: 'OFAC SDN', tier: 'hit' },
  'ruben oseguera gonzalez': {
    listName: 'OFAC SDN',
    tier: 'review',
    matchedName: 'OSEGUERA CERVANTES, Ruben',
  },
  // Waked case — the ONLY real public entry cited (OFAC SDN «WAKED HATUM, Nidal
  // Ahmed» / Waked Money Laundering Organization). Screening the FICTIONAL client
  // Yamil Waked Sánchez surfaces this as a `review` (posible coincidencia por
  // homonimia de apellido), NEVER a confirmed `hit`: the client's cédula/DOB
  // differ and the case resolves in adjudicación como probable falso positivo.
  // Several typed/normalized forms map to the same near-match so the live
  // screen fires whether the officer types the full name or just "Yamil Waked".
  'yamil antonio waked sanchez': {
    listName: 'OFAC SDN',
    tier: 'review',
    matchedName: 'WAKED HATUM, Nidal Ahmed',
    score: 88,
  },
  'yamil waked sanchez': {
    listName: 'OFAC SDN',
    tier: 'review',
    matchedName: 'WAKED HATUM, Nidal Ahmed',
    score: 88,
  },
  'yamil waked': {
    listName: 'OFAC SDN',
    tier: 'review',
    matchedName: 'WAKED HATUM, Nidal Ahmed',
    score: 88,
  },
};

/** Accent-fold + lowercase + strip punctuation — the backend stub's normalization. */
const normName = (s: string): string =>
  s
    .normalize('NFD')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

interface DemoScreenHit {
  listName: string;
  tier: 'hit' | 'review';
  matchedName: string;
  score: number;
}

/** Screen a single name against the demo watchlist; null when clear. */
const screenName = (name: string): DemoScreenHit | null => {
  const w = DEMO_WATCHLIST[normName(name)];
  if (!w) return null;
  return {
    listName: w.listName,
    tier: w.tier,
    matchedName: w.matchedName ?? name,
    score: w.score ?? (w.tier === 'hit' ? 100 : 67),
  };
};

/** Numeric family points → a qualitative Peso, using the shared bands (medio≥40, alto≥70). */
const pesoFromPoints = (points: number): Peso =>
  points >= 70 ? 'ALTO' : points >= 40 ? 'MEDIO' : 'BAJO';

/**
 * Derive the risk factor rows the detail card renders from a scored expediente.
 * The four matrix families become weighted rows; each fired override (PEP /
 * sanciones) is prepended as a CRÍTICO row so the "piso ALTO" is visible.
 */
function factorsFromComputation(comp: RiskComputation, persona: Persona): RiskFactor[] {
  const rows: RiskFactor[] = [
    { name: persona === 'legal' ? 'Perfil de la empresa' : 'Perfil del cliente', source: 'Matriz', weight: pesoFromPoints(comp.familyPoints.cliente) },
    { name: 'Jurisdicción / país', source: 'Matriz', weight: pesoFromPoints(comp.familyPoints.jurisdiccion) },
    { name: 'Canal / operación', source: 'Matriz', weight: pesoFromPoints(comp.familyPoints.canal) },
    { name: 'Producto', source: 'Matriz', weight: pesoFromPoints(comp.familyPoints.producto) },
  ];
  if (comp.overridesFired.includes('sanciones'))
    rows.unshift({ name: 'Coincidencia en listas', source: 'Screening', weight: 'CRÍTICO' });
  if (comp.overridesFired.includes('pep'))
    rows.unshift({ name: 'Estatus PEP', source: 'Screening', weight: 'CRÍTICO' });
  return rows;
}

/**
 * Score a fresh intake against the shared risk engine, honoring the wizard KYC:
 * `riskInputs` catalog keys derive the family bands, the PEP/sanciones overrides
 * fix a piso ALTO, and the composite buckets into bajo/medio/alto (medio≥40,
 * alto≥70). A clean, non-watchlisted, non-PEP persona natural scores `bajo`. The
 * demo firm is an aseguradora (entity mode `seguros`).
 */
function scoreIntake(input: NewExpedienteInput): {
  riesgo: NivelRiesgo;
  score: number;
  pep: boolean;
  factors: RiskFactor[];
} {
  const comp = computeExpedienteRisk({
    riskInputs: input.riskInputs ?? {},
    entityMode: 'seguros',
    matrix: RECOMMENDED_RISK_MATRIX,
    pepPropio: input.riskInputs?.pep,
    sancionesHit: input.riskInputs?.sanciones,
  });
  const overridden = comp.overridesFired.length > 0 && comp.scoreNivel !== 'alto';
  // When an override raises the floor to ALTO the composite alone can read low;
  // surface a score consistent with the band so the card never shows "alto · 12".
  const score = overridden ? Math.max(comp.score, 72) : comp.score;
  return {
    riesgo: comp.nivel,
    score,
    pep: comp.overridesFired.includes('pep'),
    factors: factorsFromComputation(comp, input.persona),
  };
}

/**
 * Build (but do NOT insert) a fresh expediente from an intake input — shared by
 * the intake wizard (`createExpediente`) and the copilot's `create_expediente`
 * write action so both produce identical initial shapes/history. The risk the
 * rep configured in the wizard (KYC + PEP toggles) is scored at creation time so
 * the expediente shows its band/score before validations even run.
 */
function buildExpediente(input: NewExpedienteInput): Expediente {
  const id = `EXP-2026-${String(expSeq++).padStart(4, '0')}`;
  const { riesgo, score, pep, factors } = scoreIntake(input);
  return {
    id,
    persona: input.persona,
    name: input.name,
    kind: input.kind || (input.persona === 'legal' ? 'Persona Jurídica' : 'Persona Natural'),
    icon: input.persona === 'legal' ? '🏛️' : '👤',
    docId: input.docId,
    sector: input.sector,
    email: input.email ?? null,
    phone: input.phone ?? null,
    dateOfBirth: input.dateOfBirth ?? null,
    folio: input.folio ?? null,
    constitutionDate: input.constitutionDate ?? null,
    direccionFisica: input.direccionFisica ?? null,
    usPerson: input.usPerson ?? null,
    paisResidenciaFiscal: input.paisResidenciaFiscal ?? null,
    multiplesNacionalidades: input.multiplesNacionalidades ?? null,
    direccion: input.direccion ?? null,
    started: new Date().toISOString().slice(0, 10),
    estado: 'Nuevo',
    riesgo,
    score,
    pep,
    analyst: null,
    review: null,
    fields: [
      { k: input.persona === 'legal' ? 'RUC' : 'Cédula', v: input.docId },
      { k: 'Sector', v: input.sector },
      { k: 'Inicio relación', v: new Date().toLocaleDateString('es-PA', { day: '2-digit', month: 'short', year: 'numeric' }) },
      { k: 'Estado', v: 'Captura inicial' },
    ],
    docs: [],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Pendiente', estado: 'pending', result: 'Pendiente' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Pendiente', estado: 'pending', result: 'Pendiente' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Pendiente', estado: 'pending', result: 'Pendiente' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Pendiente', estado: 'pending', result: 'Pendiente' },
      ...(input.persona === 'legal'
        ? [{ key: 'reg', icon: '🏛️', title: 'Registro Público', provider: 'Pendiente', estado: 'pending' as const, result: 'Pendiente' }]
        : []),
    ],
    factors,
    ubo: input.persona === 'legal' ? [] : null,
    history: [{ ts: stamp(), actor: 'Ana Robles', role: 'Oficial', action: 'creó el expediente' }],
  };
}

/**
 * Crea (demo) un expediente persona-natural para un beneficiario final y lo
 * inserta en la lista, devolviendo su id. Mirror en memoria del auto-create del
 * backend (createOwnerHandler): al registrar un owner natural sin
 * kycExpedienteId, nace su propio expediente KYC y se vincula.
 */
function spawnNaturalExpediente(name: string, docId: string, opts?: { pep?: boolean }): string {
  const child = buildExpediente({
    persona: 'natural',
    name,
    docId: docId || '—',
    sector: 'Beneficiario final',
    kind: 'Persona Natural · Beneficiario final',
  });
  if (opts?.pep) child.pep = true;
  expedientes = [child, ...expedientes];
  return child.id;
}

// ---- Seed: cadena de propiedad MULTI-HOP (demo) --------------------------------
// Siembra una estructura de varios saltos para el expediente jurídico
// EXP-2026-0042 (Inversiones Bellavista, S.A.) para que el grafo de la pestaña
// Estructura y la vista inversa "Vínculos" sean demostrables sin captura manual:
//
//   Inversiones Bellavista, S.A.  (raíz — la empresa del expediente)
//     ├─ Grupo Pacífico Holdings, S.A.  (jurídica · 51 %)
//     │    └─ Atlántico Investments Ltd.  (jurídica intermedia · 100 %)
//     │         ├─ Marisol Him de Spiegel  (natural · beneficiaria final · PEP · 60 %)
//     │         └─ Ricardo Achong Vega  (natural · beneficiario final · 40 %)
//     ├─ Eduardo Spiegel C.  (natural · beneficiario final · 25 %)
//     └─ Fundación Mar Adentro  (jurídica · 24 %)
//          └─ Rosa Delgado Pinzón  (natural · beneficiaria final · 100 %)
//
// Cada hoja natural tiene su propio expediente persona-natural auto-creado
// (kycExpedienteId) — el enlace que lee la vista inversa de Vínculos.
(function seedOwnershipChain() {
  const parentId = 'EXP-2026-0042';
  const marisolExp = spawnNaturalExpediente('Marisol Him de Spiegel', 'Cédula 8-234-567', {
    pep: true,
  });
  const eduardoExp = spawnNaturalExpediente('Eduardo Spiegel C.', 'Cédula 8-711-2044');
  const ricardoExp = spawnNaturalExpediente('Ricardo Achong Vega', 'Cédula 8-456-901');
  const rosaExp = spawnNaturalExpediente('Rosa Delgado Pinzón', 'Cédula 3-112-448');

  const mkOwner = (
    o: Pick<Owner, 'name' | 'ownerType' | 'ownershipPct'> & Partial<Owner>
  ): Owner => ({
    id: richId('own'),
    expedienteId: parentId,
    subjectRef: parentId,
    parentOwnerId: o.parentOwnerId ?? null,
    name: o.name,
    ownerType: o.ownerType,
    ownershipPct: o.ownershipPct,
    idNumber: o.idNumber ?? null,
    country: o.country ?? null,
    kycComplete: o.kycComplete ?? false,
    kycExpedienteId: o.kycExpedienteId ?? null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });

  const grupo = mkOwner({
    name: 'Grupo Pacífico Holdings, S.A.',
    ownerType: 'legal',
    ownershipPct: 51,
    country: 'Panamá',
    kycComplete: true,
  });
  const atlantico = mkOwner({
    name: 'Atlántico Investments Ltd.',
    ownerType: 'legal',
    ownershipPct: 100,
    parentOwnerId: grupo.id,
    country: 'Islas Vírgenes Británicas',
  });
  const marisol = mkOwner({
    name: 'Marisol Him de Spiegel',
    ownerType: 'natural',
    ownershipPct: 60,
    parentOwnerId: atlantico.id,
    idNumber: 'Cédula 8-234-567',
    country: 'Panamá',
    kycComplete: true,
    kycExpedienteId: marisolExp,
  });
  const ricardo = mkOwner({
    name: 'Ricardo Achong Vega',
    ownerType: 'natural',
    ownershipPct: 40,
    parentOwnerId: atlantico.id,
    idNumber: 'Cédula 8-456-901',
    country: 'Panamá',
    kycComplete: true,
    kycExpedienteId: ricardoExp,
  });
  const eduardo = mkOwner({
    name: 'Eduardo Spiegel C.',
    ownerType: 'natural',
    ownershipPct: 25,
    idNumber: 'Cédula 8-711-2044',
    country: 'Panamá',
    kycComplete: true,
    kycExpedienteId: eduardoExp,
  });
  const fundacion = mkOwner({
    name: 'Fundación Mar Adentro',
    ownerType: 'legal',
    ownershipPct: 24,
    country: 'Panamá',
    kycComplete: true,
  });
  const rosa = mkOwner({
    name: 'Rosa Delgado Pinzón',
    ownerType: 'natural',
    ownershipPct: 100,
    parentOwnerId: fundacion.id,
    idNumber: 'Cédula 3-112-448',
    country: 'Panamá',
    kycComplete: true,
    kycExpedienteId: rosaExp,
  });

  ownersStore[parentId] = [grupo, atlantico, marisol, ricardo, eduardo, fundacion, rosa];
})();

/**
 * Screen the client against the demo watchlist, advance the matching expediente
 * (validations + risk + estado + history), and return the sanctions run. Shared
 * by `runValidation` and the copilot's `run_validation` write action.
 */
function runSanctions(key: string, body: RunValidationBody): ValidationRun {
  const e = expedientes.find((x) => x.docId === body.subjectId || x.id === body.subjectId);
  const hit = screenName(body.fullName || e?.name || '');
  // Sanctions (scr) row outcome: [estado, result text, provider/list label].
  const scr: [Expediente['validations'][number]['estado'], string, string] =
    hit?.tier === 'hit'
      ? ['hit', 'Coincidencia en lista', hit.listName]
      : hit?.tier === 'review'
        ? ['review', 'Posible coincidencia — revisar', hit.listName]
        : ['ok', 'Sin coincidencias', 'OFAC · ONU · UE · Reino Unido · Canadá'];
  if (e) {
    const allPending = e.validations.every((v) => v.estado === 'pending');
    if (allPending) {
      const sample: Record<string, [Expediente['validations'][number]['estado'], string]> = {
        svi: ['ok', 'Validado'],
        apc: ['ok', 'Score 731'],
        scr: [scr[0], scr[1]],
        dom: ['ok', 'Coincide'],
        reg: ['ok', 'Vigente'],
      };
      e.validations = e.validations.map((v) => {
        const row = {
          ...v,
          estado: sample[v.key]?.[0] ?? 'ok',
          result: sample[v.key]?.[1] ?? 'OK',
          provider:
            v.key === 'scr' ? scr[2] : v.provider === 'Pendiente' ? 'Completado' : v.provider,
        };
        // On the sanctions row, surface the matched list entry + similarity so an
        // analyst sees why it's a hit/review (clean screens carry no match line).
        return v.key === 'scr' && hit
          ? { ...row, detail: `«${hit.matchedName}» · similitud ${hit.score}%` }
          : row;
      });
      // Risk follows the screening outcome: a hit is blocking under Ley 23
      // (alto), a review-band near-match warrants reinforced diligence (medio),
      // a clean screen keeps the client low-risk (bajo).
      if (scr[0] === 'hit') {
        e.riesgo = 'alto';
        e.score = 85;
        e.factors = [
          { name: 'Coincidencia en listas', source: 'Screening', weight: 'CRÍTICO' },
          { name: 'Actividad', source: 'Manual', weight: 'MEDIO' },
          { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
        ];
      } else if (scr[0] === 'review') {
        e.riesgo = 'medio';
        e.score = 55;
        e.factors = [
          { name: 'Posible coincidencia (revisar)', source: 'Screening', weight: 'ALTO' },
          { name: 'Actividad', source: 'Manual', weight: 'MEDIO' },
          { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
        ];
      } else {
        e.riesgo = 'bajo';
        e.score = 22;
        e.factors = [
          { name: 'Actividad', source: 'Manual', weight: 'BAJO' },
          { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
          { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
          { name: 'Score APC', source: 'APC', weight: 'MEDIO' },
        ];
      }
    }
    if (e.estado === 'Nuevo') e.estado = 'En Revisión';
    e.history = [
      { ts: stamp(), actor: 'Ana Robles', role: 'Oficial', action: 'ejecutó validaciones automáticas' },
      ...e.history,
    ];
  }
  pushAudit({
    action: 'validation.run',
    entityType: 'validation',
    entityId: `VR-${key.toUpperCase()}-${(e?.id ?? body.subjectId).replace(/[^A-Za-z0-9]/g, '')}`,
    actorRole: 'analyst',
    hasEvidence: true,
    evidenceName: `Cotejo de listas — ${scr[2]}`,
  });
  return {
    id: `vr-${Math.random().toString(36).slice(2, 8)}`,
    key,
    subjectType: body.subjectType,
    subjectId: body.subjectId,
    estado: scr[0],
    summary: scr[1],
    evidence: hit
      ? [{ label: hit.listName, value: `${hit.matchedName} (${hit.score}%)` }]
      : [{ label: 'Resultado', value: 'Sin coincidencias' }],
    providerRef: `DEMO-${key.toUpperCase()}`,
    ranAt: new Date().toISOString(),
  };
}

// --- Copilot (propose → confirm → execute) demo helpers ---

/** Fuzzy-match a free-text term against the expediente client base. */
function matchClientes(term: string): Expediente[] {
  const t = normName(term);
  if (t.length < 2) return [];
  const toks = t.split(' ').filter((w) => w.length >= 3);
  return expedientes.filter((e) => {
    const n = normName(e.name);
    return n.includes(t) || toks.some((w) => n.includes(w));
  });
}

/** Fuzzy-match a free-text term against the alert list (by client label). */
function matchAlertas(term: string): Alert[] {
  const t = normName(term);
  if (t.length < 2) return [];
  const toks = t.split(' ').filter((w) => w.length >= 3);
  return alerts.filter((a) => {
    const n = normName(a.cliente);
    return n.includes(t) || toks.some((w) => n.includes(w));
  });
}

/** Pull the client name that trails an intent phrase ("... a/para/de <nombre>"). */
function trailingName(message: string, lead: RegExp): string {
  const m = message.match(lead);
  return (m?.[1] ?? '').replace(/[?.!¿¡]+\s*$/g, '').trim();
}

const proposeChat = (
  answer: string,
  action: AssistantProposedAction
): AssistantChatResponse => ({ answer, toolsUsed: [action.tool], proposedActions: [action] });

const plainChat = (answer: string, toolsUsed: string[] = []): AssistantChatResponse => ({
  answer,
  toolsUsed,
});

/**
 * Demo-only projection of a qualitative factor weight (Peso) onto the numeric
 * evidence shape the real `explain_client_risk` tool emits — a weight % for the
 * factor label and a `banda · N pts` value the risk card parses into a bar. Keeps
 * the demo cards visually faithful to production without inventing a matrix.
 */
const DEMO_PESO_EVIDENCE: Record<Peso, { band: string; pts: number; weightPct: number }> = {
  CRÍTICO: { band: 'alto', pts: 30, weightPct: 30 },
  ALTO: { band: 'alto', pts: 22, weightPct: 25 },
  MEDIO: { band: 'medio', pts: 13, weightPct: 20 },
  BAJO: { band: 'bajo', pts: 6, weightPct: 15 },
};

/** An alert is "abierta" until it lands in a resolved/closed/dismissed estado. */
const isOpenAlert = (a: Alert): boolean => !/(resuelt|cerrad|descartad)/i.test(a.estado);

/** Build the AssistantExecuteResponse envelope (entity omitted when absent). */
const execResult = (
  tool: string,
  result: unknown,
  entity?: { type: string; id: string }
): AssistantExecuteResponse => ({
  tool,
  status: 'executed',
  result,
  ...(entity ? { entity } : {}),
});

const asStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

// --- §3 Renewal (renovación de debida diligencia) demo helpers ---

/**
 * Compute the due renewals from the demo expedientes: an expediente is "due"
 * when it has an evaluated risk nivel (bajo/medio/alto), a parseable next-review
 * date, and that date falls on/before today OR within `windowDays`. Deterministic
 * — mirrors the API due-detection (por-riesgo cadence, windowDays horizon).
 */
function computeDueRenewals(windowDays: number): DueRenewalResponse[] {
  const today = todayIso();
  const out: DueRenewalResponse[] = [];
  for (const e of expedientes) {
    if (e.riesgo === 'sin') continue;
    const nextReviewAt = parseReviewDate(e.review);
    if (!nextReviewAt) continue;
    const dueInDays = daysUntil(nextReviewAt, today);
    if (Number.isNaN(dueInDays) || dueInDays > windowDays) continue;
    out.push({
      scheduledReviewId: `SR-${e.id}`,
      expedienteId: e.id,
      expedienteRef: e.id,
      nombre: e.name,
      nivel: e.riesgo,
      cadenceMonths: RISK_CADENCE_MONTHS[e.riesgo],
      scheduledFor: nextReviewAt,
      nextReviewAt,
      dueInDays,
      alertOpen: renewalEmitted.has(`${e.id}:${nextReviewAt}`),
    });
  }
  // Most overdue first (smallest/most-negative dueInDays leads).
  return out.sort((a, b) => a.dueInDays - b.dueInDays);
}

/**
 * The FULL copilot capability catalog for demo mode (es-PA copy mirrored from the
 * API's tool-catalog). Demo has no RBAC, so every caller sees all 15 tools; the
 * real app RBAC-filters this server-side. `category` follows `kind` (read →
 * 'consulta', write → 'accion'), matching the server's derivation.
 */
const DEMO_ASSISTANT_TOOLS: readonly AssistantToolInfo[] = [
  // --- read tools (category 'consulta') ---
  { name: 'find_client', kind: 'read', category: 'consulta', label: 'Buscar cliente', description: 'Encuentra un cliente por su nombre o documento y muestra su estado y nivel de riesgo.', prompt: 'Busca al cliente ' },
  { name: 'list_clients', kind: 'read', category: 'consulta', label: 'Ver clientes', description: 'Muestra los clientes recientes del tenant con su estado y nivel de riesgo.', prompt: 'Muéstrame los clientes recientes' },
  { name: 'explain_client_risk', kind: 'read', category: 'consulta', label: 'Explicar riesgo del cliente', description: 'Explica por qué un cliente tiene su nivel de riesgo y qué factores lo componen.', prompt: 'Explícame por qué el cliente ' },
  { name: 'list_missing_documents', kind: 'read', category: 'consulta', label: 'Documentos pendientes', description: 'Revisa qué documentos le faltan a un cliente y cuáles están por vencer o vencidos.', prompt: '¿Qué documentos le faltan al cliente ' },
  { name: 'screen_name', kind: 'read', category: 'consulta', label: 'Verificar en listas', description: 'Coteja un nombre contra las listas de sanciones y PEP.', prompt: 'Verifica el nombre ' },
  { name: 'get_dashboard_summary', kind: 'read', category: 'consulta', label: 'Resumen del panel', description: 'Resume tus expedientes por estado y riesgo, y cuántas alertas siguen abiertas.', prompt: 'Dame un resumen de cómo vamos' },
  { name: 'list_alerts', kind: 'read', category: 'consulta', label: 'Ver alertas', description: 'Lista las alertas recientes y los casos pendientes de investigación.', prompt: 'Muéstrame las alertas recientes' },
  { name: 'explain_risk_matrix', kind: 'read', category: 'consulta', label: 'Explicar la matriz de riesgo', description: 'Explica los pesos, umbrales y el fundamento de la matriz de riesgo del tenant.', prompt: '¿Cómo está configurada la matriz de riesgo y de dónde salen los pesos?' },
  { name: 'parse_rp_document', kind: 'read', category: 'consulta', label: 'Leer documento del Registro Público', description: 'Extrae folio, estado, junta directiva, gravámenes y alertas de un documento descargado del Registro Público.', prompt: 'Lee este documento del Registro Público: ' },
  { name: 'explain_compliance_obligations', kind: 'read', category: 'consulta', label: 'Obligaciones de la Ley 23', description: 'Explica tus obligaciones de cumplimiento y prevención bajo la Ley 23 y el D.E. 35, con su artículo y cita textual.', prompt: '¿Qué obligaciones de cumplimiento tengo bajo la Ley 23?' },
  // --- write tools (category 'accion') ---
  { name: 'create_expediente', kind: 'write', category: 'accion', label: 'Crear expediente', description: 'Abre un expediente nuevo para un cliente. Queda propuesto hasta que lo confirmes.', prompt: 'Crea un expediente nuevo para ' },
  { name: 'update_expediente', kind: 'write', category: 'accion', label: 'Actualizar expediente', description: 'Cambia el estado o la persona asignada de un expediente. Queda propuesto hasta que lo confirmes.', prompt: 'Cambia el estado del expediente ' },
  { name: 'request_documents', kind: 'write', category: 'accion', label: 'Solicitar documentos', description: 'Envía al cliente una solicitud de sus documentos pendientes. Queda propuesta hasta que la confirmes.', prompt: 'Solicita al cliente sus documentos pendientes para el expediente ' },
  { name: 'update_alert', kind: 'write', category: 'accion', label: 'Actualizar alerta', description: 'Cambia el estado de una alerta con una nota que justifique el cambio. Queda propuesta hasta que la confirmes.', prompt: 'Cambia el estado de la alerta ' },
  { name: 'create_review', kind: 'write', category: 'accion', label: 'Programar revisión', description: 'Agenda una revisión de un expediente para una fecha. Queda propuesta hasta que la confirmes.', prompt: 'Programa una revisión del expediente ' },
  { name: 'run_validation', kind: 'write', category: 'accion', label: 'Ejecutar validación', description: 'Corre una validación de cumplimiento (sanciones, PEP, identidad, riesgo, etc.) sobre un sujeto. Queda propuesta hasta que la confirmes.', prompt: 'Ejecuta la validación de ' },
];

/**
 * Expediente activity log (demo synthesis) — a realistic, newest-first feed for
 * the redesigned "Actividad" / "Versiones y restauración" surfaces, built from
 * the expediente's own `history` seed plus its documents/review/consent/alert
 * stores. Not backed by a real audit trail (the demo has no DynamoDB), but the
 * action codes and entityType values match the real `esPA.auditoria.actions` /
 * `esPA.expedienteActivity.categories` catalogs, so the UI renders identically
 * to cumpli-live. `riesgo`/`score` are never restored verbatim — same guarantee
 * the real backend makes (re-derives from risk inputs on restore).
 */
function buildExpedienteActivity(id: string): ExpedienteActivityEntry[] {
  const e = expedientes.find((x) => x.id === id);
  if (!e) return [];

  const entries: ExpedienteActivityEntry[] = [];
  let seq = 0;
  const nextKey = () => `demo-${e.id}-act-${seq++}`;

  // --- Expediente-level history entries (same `history` seed as
  // `buildExpedienteVersions`) — informational only in this lane. ---
  const events =
    e.history.length > 0
      ? e.history
      : [{ ts: stamp(), actor: me.fullName, role: 'Oficial', action: 'creó el expediente' }];
  const snapshotAt = (stepsBack: number): Record<string, unknown> => ({
    nombre: e.name,
    estado: stepsBack === 0 ? e.estado : 'En Revisión',
    pep: e.pep,
    assignedTo: e.analyst,
    nextReviewAt: e.review,
    riesgo: e.riesgo,
    score: Math.max(0, e.score - stepsBack * 5),
  });
  // Each `events[i]` is the SAME history step `buildExpedienteVersions` turns
  // into its version-projection row at that same index — so this loop pairs
  // 1:1 with that one, index-for-index (both walk newest-first). Mirrors the
  // real backend's decoration: these entries carry the paired row's
  // `versionSeq` + `restorable: true`; every other synthetic entry below
  // (decision/document/review/consent/alert) has no paired projection row in
  // this demo synthesis, so it stays `versionSeq: null, restorable: false`.
  events.forEach((h, i) => {
    const isOldest = i === events.length - 1;
    entries.push({
      versionKey: `demo-${e.id}-v${i}`,
      action: isOldest
        ? 'expediente.created'
        : h.action.includes('restaur')
          ? 'expediente.version_restored'
          : 'expediente.updated',
      entityType: 'expediente',
      entityId: e.id,
      targetLabel: null,
      actorName: h.actor,
      actorRole: h.role,
      createdAt: stampToIso(h.ts),
      beforeState: isOldest ? null : snapshotAt(i + 1),
      afterState: snapshotAt(i),
      versionSeq: events.length - i,
      restorable: true,
    });
  });

  // --- Decision reflecting the current estado (approved/escalated) — newer
  // than every version-trail entry above. ---
  const latestIso = stampToIso(events[0]?.ts ?? stamp());
  const decisionAt = new Date(new Date(latestIso).getTime() + 5 * 60_000).toISOString();
  if (e.estado === 'Activo' || e.estado === 'Aprobado') {
    entries.unshift({
      versionKey: nextKey(),
      action: 'expediente.approved',
      entityType: 'expediente',
      entityId: e.id,
      targetLabel: null,
      actorName: e.analyst ?? me.fullName,
      actorRole: 'compliance_manager',
      createdAt: decisionAt,
      beforeState: null,
      afterState: null,
      versionSeq: null,
      restorable: false,
    });
  } else if (e.estado === 'Escalado') {
    entries.unshift({
      versionKey: nextKey(),
      action: 'expediente.escalated',
      entityType: 'expediente',
      entityId: e.id,
      targetLabel: null,
      actorName: e.analyst ?? me.fullName,
      actorRole: 'analyst',
      createdAt: decisionAt,
      beforeState: null,
      afterState: null,
      versionSeq: null,
      restorable: false,
    });
  }

  // --- Documents: the most recent upload, plus one recoverable deletion. ---
  ensureDocsSeeded(id);
  const latestDoc = docsStore[id]?.[0];
  if (latestDoc) {
    entries.push({
      versionKey: nextKey(),
      action: 'expediente.document.uploaded',
      entityType: 'expediente_document',
      entityId: latestDoc.id,
      targetLabel: latestDoc.name,
      actorName: 'Roberto Achong',
      actorRole: 'intake_officer',
      createdAt: latestDoc.createdAt,
      beforeState: null,
      afterState: { category: latestDoc.category, estado: latestDoc.estado },
      versionSeq: null,
      restorable: false,
    });
  }
  const deletedDoc = ensureDeletedDocSeed(id);
  if (deletedDoc) {
    entries.push({
      versionKey: nextKey(),
      action: 'expediente.document.deleted',
      entityType: 'expediente_document',
      entityId: deletedDoc.id,
      targetLabel: deletedDoc.name,
      actorName: me.fullName,
      actorRole: 'compliance_manager',
      createdAt: deletedDoc.updatedAt,
      beforeState: { category: deletedDoc.category, estado: deletedDoc.estado },
      afterState: null,
      versionSeq: null,
      restorable: false,
    });
  }

  // --- Scheduled review, if this expediente has one. ---
  const review = reviewsStore[id]?.[0];
  if (review) {
    entries.push({
      versionKey: nextKey(),
      action: 'review.updated',
      entityType: 'scheduled_review',
      entityId: review.id,
      targetLabel: `Revisión ${review.reviewType === 'periodica' ? 'periódica' : review.reviewType}`,
      actorName: review.assignedTo ?? me.fullName,
      actorRole: 'analyst',
      createdAt: review.updatedAt,
      beforeState: null,
      afterState: { status: review.status, scheduledFor: review.scheduledFor },
      versionSeq: null,
      restorable: false,
    });
  }

  // --- Credit/registro consent, if recorded. ---
  const consent = consentsStore[id]?.[0];
  if (consent) {
    entries.push({
      versionKey: nextKey(),
      action: 'consent.created',
      entityType: 'credit_consent',
      entityId: consent.id,
      targetLabel: consent.source,
      actorName: consent.recordedBy ?? me.fullName,
      actorRole: 'intake_officer',
      createdAt: consent.createdAt,
      beforeState: null,
      afterState: null,
      versionSeq: null,
      restorable: false,
    });
  }

  // --- Open alert on this expediente, if any. ---
  const alert = alerts.find((a) => a.expedienteId === id);
  if (alert) {
    entries.push({
      versionKey: nextKey(),
      action: 'alert.updated',
      entityType: 'alert',
      entityId: alert.id,
      targetLabel: alert.tipo,
      actorName: alert.asignada ?? me.fullName,
      actorRole: 'compliance_manager',
      createdAt: alert.abierta ?? nowIso(),
      beforeState: null,
      afterState: { estado: alert.estado },
      versionSeq: null,
      restorable: false,
    });
  }

  return entries.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/**
 * Expediente version projection (demo synthesis) — a realistic, newest-first
 * feed for the "Versiones" lane (Lane B, restorable), built from the SAME
 * `history` seed as `buildExpedienteActivity`'s version trail, but standing
 * in for the real Postgres `expediente_versions` CQRS projection: each row is
 * a full case-aggregate snapshot (`{ schemaVersion, expediente, documents }`),
 * `versionSeq` monotonic ascending (oldest = 1), `restorable` always true —
 * matching the real `GET /expedientes/{id}/versions` contract. `riesgo`/
 * `score` are never restored verbatim on restore (re-derived server-side),
 * same guarantee the real backend makes.
 */
function buildExpedienteVersions(id: string): ExpedienteVersionResponse[] {
  const e = expedientes.find((x) => x.id === id);
  if (!e) return [];
  ensureDocsSeeded(id);

  const events =
    e.history.length > 0
      ? e.history
      : [{ ts: stamp(), actor: me.fullName, role: 'Oficial', action: 'creó el expediente' }];
  const snapshotAt = (stepsBack: number): Record<string, unknown> => ({
    schemaVersion: 1,
    expediente: {
      nombre: e.name,
      estado: stepsBack === 0 ? e.estado : 'En Revisión',
      pep: e.pep,
      assignedTo: e.analyst,
      nextReviewAt: e.review,
      riesgo: e.riesgo,
      score: Math.max(0, e.score - stepsBack * 5),
    },
    documents: (docsStore[id] ?? []).map((d) => ({ id: d.id, category: d.category, estado: d.estado })),
  });

  const total = events.length;
  // `events` is newest-first (index 0 = most recent); versionSeq is the
  // monotonic-ascending real-backend key, so the oldest event gets 1.
  return events.map((h, i) => {
    const isOldest = i === total - 1;
    return {
      versionSeq: total - i,
      action: isOldest
        ? 'expediente.created'
        : h.action.includes('restaur')
          ? 'expediente.version_restored'
          : 'expediente.updated',
      actorName: h.actor,
      actorRole: h.role,
      createdAt: stampToIso(h.ts),
      beforeState: isOldest ? null : snapshotAt(i + 1),
      afterState: snapshotAt(i),
      restorable: true,
    };
  });
}

export const demoApi = {
  me: () => delay(me),
  roles: () => delay(roles),

  listUsers: (params: { search?: string; status?: string } = {}) => {
    // Deleted users are hidden unless explicitly requested (mirrors the API's
    // default list, which excludes soft-deleted rows).
    let out = params.status ? users.filter((u) => u.status === params.status) : users.filter((u) => u.status !== 'deleted');
    if (params.search) {
      const q = params.search.toLowerCase();
      out = out.filter(
        (u) => u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      );
    }
    return delay(out.map((u) => ({ ...u })));
  },

  getUser: (id: string) => {
    const u = users.find((x) => x.id === id);
    if (!u) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'No encontrado'));
    return delay({ ...u });
  },

  createUser: (body: { fullName: string; email: string; roleKeys: string[] }) => {
    if (users.some((u) => u.email.toLowerCase() === body.email.toLowerCase())) {
      return Promise.reject(
        new RequestError(409, 'CONFLICT', 'Ya existe un usuario con ese correo')
      );
    }
    const u: UserResponse = {
      id: nextId('u'),
      fullName: body.fullName,
      email: body.email,
      roles: body.roleKeys,
      status: 'active',
      lastLoginAt: null,
    };
    users = [u, ...users];
    return delay(u);
  },

  updateUser: (id: string, body: { fullName?: string; status?: string }) => {
    const u = users.find((x) => x.id === id);
    if (!u) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'No encontrado'));
    if (body.fullName !== undefined) u.fullName = body.fullName;
    if (body.status) u.status = body.status as UserResponse['status'];
    return delay({ ...u });
  },

  assignRoles: (id: string, roleKeys: string[]) => {
    const u = users.find((x) => x.id === id);
    if (!u) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'No encontrado'));
    u.roles = roleKeys;
    return delay({ ...u });
  },

  deactivateUser: (id: string) => {
    const u = users.find((x) => x.id === id);
    if (!u) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'No encontrado'));
    u.status = 'deactivated';
    return delay({ ...u });
  },
  reactivateUser: (id: string) => {
    const u = users.find((x) => x.id === id);
    if (!u) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'No encontrado'));
    u.status = 'active';
    return delay({ ...u });
  },

  // Soft-delete: mirrors the backend guard — only a `deactivated` user may be
  // deleted; an active one returns 409 MUST_DEACTIVATE_FIRST. The row is kept
  // but marked `deleted` (filtered out of the default list, like the real API).
  deleteUser: (id: string) => {
    const u = users.find((x) => x.id === id);
    if (!u) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'No encontrado'));
    if (u.status === 'active') {
      return Promise.reject(
        new RequestError(409, 'MUST_DEACTIVATE_FIRST', 'Primero debes desactivar al usuario')
      );
    }
    u.status = 'deleted';
    return delay(undefined as void);
  },

  listTenants: () => delay(tenants.map((t) => ({ ...t }))),

  // --- Conversaciones de IA (lectura supervisoria, solo super_admin) ---
  // Muestra sembrada: el demo no persiste bitácora, pero la pantalla debe
  // renderizar con datos creíbles (misma forma que los registros reales).
  listAssistantLog: (params: {
    tenantId: string;
    conversationId?: string;
    cursor?: string;
    limit?: number;
  }): Promise<AssistantLogListResponse> => {
    const base = {
      tenantId: params.tenantId,
      userId: 'u-002',
      roles: ['compliance_manager'],
      modelId: 'claude-haiku-4-5',
      promptVersion: 'v3',
      schemaVersion: 2,
    };
    const items: AssistantLogRecord[] = [
      {
        recordType: 'turn',
        conversationId: 'demo-conv-2',
        turnId: 'demo-t3',
        seq: 1,
        ts: '2026-07-15T16:40:00.000Z',
        userMessage: 'Explícame el riesgo de Casa de Cambio y Remesas Centroamérica',
        answer:
          'Casa de Cambio y Remesas Centroamérica, S.A. está clasificada en riesgo ALTO (79/100), calculado con la matriz vigente. Lo que más pesa es la actividad (remesadora · efectivo).',
        toolsUsed: ['explain_client_risk'],
        ...base,
      },
      {
        recordType: 'decision',
        conversationId: 'demo-conv-2',
        turnId: 'demo-t3',
        ts: '2026-07-15T16:42:00.000Z',
        tool: 'run_validation',
        input: { key: 'sanctions' },
        status: 'executed',
        entity: { type: 'validation_run', id: 'demo-vr-01' },
        editedFromProposal: false,
        tenantId: params.tenantId,
        userId: 'u-002',
        roles: ['compliance_manager'],
        modelId: 'claude-haiku-4-5',
        schemaVersion: 2,
      },
      {
        recordType: 'turn',
        conversationId: 'demo-conv-1',
        turnId: 'demo-t1',
        seq: 1,
        ts: '2026-07-14T14:05:00.000Z',
        userMessage: 'Dame un resumen de cómo vamos',
        answer:
          'Su cartera tiene 14 expedientes: 7 en riesgo bajo, 4 en medio y 3 en alto, con 6 alertas abiertas (2 de prioridad alta).',
        toolsUsed: ['get_dashboard_summary'],
        ...base,
      },
      {
        recordType: 'turn',
        conversationId: 'demo-conv-1',
        turnId: 'demo-t2',
        seq: 2,
        ts: '2026-07-14T14:07:00.000Z',
        userMessage: 'Muéstrame los clientes recientes',
        answer:
          'Aquí tiene su cartera (14 en total):\n\n| Nombre | Estado | Riesgo | Score |\n|--------|--------|--------|-------|\n| Yamil Antonio Waked Sánchez | Revisión especial | alto | 82 |\n| Ricardo Alberto Him Fábrega | Activo | alto | 78 |\n| Casa de Cambio y Remesas Centroamérica, S.A. | Activo | alto | 79 |\n| María Elena Castillo Vega | Activo | bajo | 18 |\n\nTres operan en riesgo alto y tienen su próxima revisión más cercana.',
        toolsUsed: ['list_clients'],
        ...base,
      },
    ];
    const filtered = params.conversationId
      ? items.filter((i) => i.conversationId === params.conversationId)
      : items;
    return delay({
      items: filtered,
      nextCursor: null,
      users: { 'u-002': { fullName: 'Julio Castillo', email: 'julio.castillo@miexito.com.pa' } },
    });
  },

  createTenant: (body: {
    legalName: string;
    planTier?: string;
    admin: { email: string; fullName: string };
  }) => {
    const tenant: TenantResponse = {
      id: nextId('t'),
      legalName: body.legalName,
      status: 'active',
      planTier: body.planTier ?? 'standard',
      createdAt: new Date().toISOString(),
      userCount: 1,
    };
    tenants = [tenant, ...tenants];
    const admin: UserResponse = {
      id: nextId('u'),
      fullName: body.admin.fullName,
      email: body.admin.email,
      roles: ['tenant_admin'],
      status: 'active',
      lastLoginAt: null,
    };
    const res: CreateTenantResponse = { tenant, admin };
    return delay(res);
  },

  updateTenant: (
    id: string,
    body: { status?: 'active' | 'suspended' | 'offboarding'; planTier?: 'standard' | 'premium' }
  ) => {
    const t = tenants.find((x) => x.id === id);
    if (!t) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'No encontrado'));
    if (body.status !== undefined) t.status = body.status;
    if (body.planTier !== undefined) t.planTier = body.planTier;
    return delay({ ...t });
  },

  listExpedientes: () => delay(expedientes.map((e) => ({ ...e }))),

  getExpediente: (id: string) => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    return delay({ ...e });
  },

  createExpediente: (input: NewExpedienteInput) => {
    const e = buildExpediente(input);
    expedientes = [e, ...expedientes];
    pushAudit({
      action: 'expediente.create',
      entityType: 'expediente',
      entityId: e.id,
      actorRole: 'intake_officer',
      beforeState: null,
      afterState: { estado: e.estado, riesgo: e.riesgo, nombre: e.name },
    });
    return delay({ ...e });
  },

  // OCR prefill: pretend to read the cédula (or, for kind 'address', a service
  // receipt) and return a canned demo result (longer delay so the "Leyendo tu
  // documento…" state is visible).
  ocrPrefill: (_file: File, kind: 'id' | 'address' = 'id'): Promise<OcrPrefillResult> =>
    kind === 'address'
      ? delay(
          {
            fields: { direccion: 'Calle 50, Ciudad de Panamá' },
            confidence: 90,
            lowConfidence: false,
          },
          900
        )
      : delay(
          {
            fields: {
              fullName: 'María González Pérez',
              idNumber: '8-712-3456',
              birthDate: '1988-03-14',
              expiryDate: '2029-03-14',
            },
            confidence: 93,
            lowConfidence: false,
          },
          900
        ),

  // Demo: "running validations" screens the client against the watchlist (so a
  // sanctioned name actually surfaces a hit/review instead of every client being
  // force-cleared), advances the matching expediente, and returns the sanctions
  // run. The UI re-fetches the expediente to render the result.
  runValidation: (key: string, body: RunValidationBody): Promise<ValidationRun> =>
    delay(runSanctions(key, body)),

  listValidationRuns: (subjectId: string): Promise<ValidationRun[]> => {
    const runs: ValidationRun[] = ['identity', 'credit', 'sanctions', 'address'].map((key) => ({
      id: `vr-${subjectId}-${key}`,
      key,
      subjectType: 'natural' as const,
      subjectId,
      estado: 'ok' as const,
      summary: 'Validado',
      evidence: [{ label: 'Resultado', value: 'Sin coincidencias' }],
      providerRef: `DEMO-${key.toUpperCase()}`,
      ranAt: new Date().toISOString(),
    }));
    return delay(runs);
  },

  setExpedienteEstado: (id: string, estado: EstadoExpediente, action: string) => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    const beforeEstado = e.estado;
    e.estado = estado;
    e.history = [{ ts: stamp(), actor: 'Ana Robles', role: 'Oficial', action }, ...e.history];
    pushAudit({
      action: 'expediente.update',
      entityType: 'expediente',
      entityId: e.id,
      beforeState: { estado: beforeEstado },
      afterState: { estado },
    });
    return delay({ ...e });
  },

  updateExpediente: (id: string, patch: UpdateExpedienteInput) => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    const before = { estado: e.estado, riesgo: e.riesgo };
    if (patch.nombre !== undefined) e.name = patch.nombre;
    if (patch.email !== undefined) e.email = patch.email;
    if (patch.phone !== undefined) e.phone = patch.phone;
    if (patch.estado !== undefined) e.estado = patch.estado;
    if (patch.riesgo !== undefined) e.riesgo = patch.riesgo;
    if (patch.score !== undefined) e.score = patch.score;
    if (patch.pep !== undefined) e.pep = patch.pep;
    if (patch.persona !== undefined) e.persona = patch.persona;
    if (patch.docId !== undefined) e.docId = patch.docId;
    if (patch.sector !== undefined) e.sector = patch.sector;
    if (patch.kind !== undefined) e.kind = patch.kind;
    if (patch.docKind !== undefined) e.docKind = patch.docKind;
    if (patch.folio !== undefined) e.folio = patch.folio;
    if (patch.canal !== undefined) e.canal = patch.canal;
    if (patch.producto !== undefined) e.producto = patch.producto;
    if (patch.dateOfBirth !== undefined) e.dateOfBirth = patch.dateOfBirth;
    if (patch.direccion !== undefined) e.direccion = patch.direccion;
    if (patch.constitutionDate !== undefined) e.constitutionDate = patch.constitutionDate;
    if (patch.nextReviewAt !== undefined) e.review = patch.nextReviewAt;
    e.history = [
      { ts: stamp(), actor: 'Ana Robles', role: 'Oficial', action: 'editó el expediente' },
      ...e.history,
    ];
    pushAudit({
      action: 'expediente.update',
      entityType: 'expediente',
      entityId: e.id,
      beforeState: before,
      afterState: { estado: e.estado, riesgo: e.riesgo },
    });
    return delay({ ...e });
  },

  deleteExpediente: (id: string): Promise<void> => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    expedientes = expedientes.filter((x) => x.id !== id);
    papelera = [toPapeleraRow(e, nowIso()), ...papelera];
    pushAudit({
      action: 'expediente.delete',
      entityType: 'expediente',
      entityId: e.id,
      beforeState: { estado: e.estado, riesgo: e.riesgo },
      afterState: null,
    });
    return delay(undefined);
  },

  restoreExpediente: (id: string): Promise<void> => {
    const row = papelera.find((x) => x.id === id);
    if (!row) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    papelera = papelera.filter((x) => x.id !== id);
    expedientes = [fromPapeleraRow(row), ...expedientes];
    return delay(undefined);
  },

  // --- Expediente version projection ("Versiones" — Lane B, restorable) ---
  // Not backed by a real Postgres projection (the demo has no database) — see
  // `buildExpedienteVersions` for the synthesis over the `history` seed.
  getExpedienteVersions: (id: string): Promise<ExpedienteVersionsResponse> => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    return delay({ versions: buildExpedienteVersions(id) });
  },

  // --- Expediente activity log (the UNIFIED, restore-decorated timeline) ---
  getExpedienteActivity: (id: string): Promise<ExpedienteActivityResponse> =>
    delay({ entries: buildExpedienteActivity(id) }),

  // A single version-projection row's full case-aggregate snapshot, by
  // `versionSeq` — mirrors the real `GET .../versions/{versionSeq}`, sourced
  // from the SAME `buildExpedienteVersions` synthesis the (removed) Lane-B
  // list used to read from.
  getExpedienteVersionDetail: (id: string, versionSeq: number): Promise<ExpedienteVersionResponse> => {
    const row = buildExpedienteVersions(id).find((v) => v.versionSeq === versionSeq);
    if (!row) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Versión no encontrada'));
    return delay(row);
  },

  // Optimistic in-memory "restore" — re-applies the target version's aggregate
  // state as a NEW history entry (never mutates the trail itself), which in
  // turn PUSHES A NEW VERSION the next time `getExpedienteVersions` is read
  // (mirroring the real backend's append-only projection). Also logged to the
  // audit feed. Deliberately leaves `riesgo`/`score` untouched rather than
  // resurrecting a historical value, the same guarantee the real backend makes
  // (re-derives from risk inputs).
  restoreExpedienteVersion: (id: string, versionSeq: number): Promise<Expediente> => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    e.history = [
      { ts: stamp(), actor: me.fullName, role: 'Oficial', action: `restauró la versión ${versionSeq}` },
      ...e.history,
    ];
    pushAudit({
      action: 'expediente.version_restored',
      entityType: 'expediente',
      entityId: e.id,
      actorRole: 'compliance_manager',
      beforeState: { estado: e.estado },
      afterState: { estado: e.estado },
    });
    return delay({ ...e });
  },

  listPapelera: (): Promise<DemoPapeleraRow[]> => delay(papelera.map((r) => ({ ...r }))),

  addDocument: (id: string, body: { name: string; category: string; ownerId?: string }) => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    e.docs = [{ name: body.name, category: body.category, expiry: null, estado: 'Recibido' }, ...e.docs];
    e.history = [
      { ts: stamp(), actor: 'Ana Robles', role: 'Oficial', action: `cargó documento — ${body.name}` },
      ...e.history,
    ];
    const doc: DemoDoc = {
      id: richId('doc'),
      expedienteId: id,
      category: body.category,
      name: body.name,
      estado: 'Recibido',
      expiry: null,
      documentTypeId: null,
      ownerId: body.ownerId ?? null,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    docsStore[id] = [doc, ...(docsStore[id] ?? [])];
    pushAudit({
      action: 'document.upload',
      entityType: 'document',
      entityId: doc.id,
      actorRole: 'intake_officer',
      hasEvidence: true,
      evidenceName: `Comprobante de carga — ${body.name}`,
    });
    return delay({ ...doc });
  },

  // Demo upload: no real S3 — just record the file as a received document.
  // `ownerId` is persisted on the DemoDoc so the per-person drill-down filters work.
  uploadDocument: (
    expedienteId: string,
    input: { file: File; category: string; ownerId?: string }
  ): Promise<ExpedienteDoc> => {
    const e = expedientes.find((x) => x.id === expedienteId);
    if (e) {
      e.docs = [
        { name: input.file.name, category: input.category, expiry: null, estado: 'Recibido' },
        ...e.docs,
      ];
      e.history = [
        { ts: stamp(), actor: 'Ana Robles', role: 'Oficial', action: `subió documento — ${input.file.name}` },
        ...e.history,
      ];
    }
    const doc: DemoDoc = {
      id: richId('doc'),
      expedienteId,
      category: input.category,
      name: input.file.name,
      estado: 'Recibido',
      expiry: null,
      documentTypeId: null,
      ownerId: input.ownerId ?? null,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    docsStore[expedienteId] = [doc, ...(docsStore[expedienteId] ?? [])];
    pushAudit({
      action: 'document.upload',
      entityType: 'document',
      entityId: doc.id,
      actorRole: 'intake_officer',
      hasEvidence: true,
      evidenceName: `Comprobante de carga — ${input.file.name}`,
    });
    return delay({ ...doc });
  },

  // --- Expediente documents (rich) ---
  listDocuments: (
    expedienteId: string,
    opts?: { deleted?: boolean; ownerId?: string }
  ): Promise<ExpedienteDoc[]> => {
    ensureDocsSeeded(expedienteId);
    const store = opts?.deleted ? deletedDocsStore : docsStore;
    const rows = (store[expedienteId] ?? []).filter(
      // Fase 4: narrow to a single person's docs when asked (parity with the API).
      (d) => opts?.ownerId === undefined || d.ownerId === opts.ownerId
    );
    return delay(rows.map((d) => ({ ...d })));
  },

  deleteDocument: (expedienteId: string, docId: string): Promise<void> => {
    const list = docsStore[expedienteId] ?? [];
    const d = list.find((x) => x.id === docId);
    if (!d) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Documento no encontrado'));
    docsStore[expedienteId] = list.filter((x) => x.id !== docId);
    deletedDocsStore[expedienteId] = [
      { ...d, updatedAt: nowIso() },
      ...(deletedDocsStore[expedienteId] ?? []),
    ];
    return delay(undefined);
  },

  restoreDocument: (expedienteId: string, docId: string): Promise<void> => {
    const list = deletedDocsStore[expedienteId] ?? [];
    const d = list.find((x) => x.id === docId);
    if (!d) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Documento no encontrado'));
    deletedDocsStore[expedienteId] = list.filter((x) => x.id !== docId);
    docsStore[expedienteId] = [{ ...d, updatedAt: nowIso() }, ...(docsStore[expedienteId] ?? [])];
    return delay(undefined);
  },

  // Demo replace: no real S3 — just swap the recorded file name, keeping estado/categoría.
  replaceDocument: (
    expedienteId: string,
    docId: string,
    file: File
  ): Promise<ExpedienteDoc> => {
    const list = docsStore[expedienteId] ?? [];
    const d = list.find((x) => x.id === docId);
    if (!d) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Documento no encontrado'));
    d.name = file.name;
    d.updatedAt = nowIso();
    return delay({ ...d });
  },

  updateDocument: (
    expedienteId: string,
    docId: string,
    patch: ExpedienteDocPatch
  ): Promise<ExpedienteDoc> => {
    const list = docsStore[expedienteId] ?? [];
    const d = list.find((x) => x.id === docId);
    if (!d) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Documento no encontrado'));
    if (patch.estado !== undefined) d.estado = patch.estado;
    if (patch.expiryDate !== undefined) d.expiry = patch.expiryDate;
    if (patch.name !== undefined) d.name = patch.name;
    d.updatedAt = nowIso();
    return delay({ ...d });
  },

  getDocumentDownloadUrl: (expedienteId: string, docId: string) => {
    const doc = (docsStore[expedienteId] ?? []).find((d) => d.id === docId);
    const e = expedientes.find((x) => x.id === expedienteId);
    return delay(
      svgDataUri(doc?.name ?? 'Documento', [
        `Expediente: ${expedienteId}`,
        `Cliente: ${e?.name ?? '—'}`,
        `Categoría: ${doc?.category ?? '—'}`,
        `Estado: ${doc?.estado ?? 'Recibido'}`,
        doc?.expiry ? `Vence: ${doc.expiry}` : 'Sin fecha de vencimiento',
        '',
        'Documento cargado en el expediente de debida diligencia (Ley 23 de 2015).',
      ]),
      400
    );
  },

  requestDocuments: (id: string, body: { channels: string[]; message?: string }) => {
    const e = expedientes.find((x) => x.id === id);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    const channels = body.channels.length > 0 ? body.channels : ['whatsapp'];
    const labels = channels.map((c) => (c === 'whatsapp' ? 'WhatsApp' : 'correo')).join(' y ');
    e.history = [
      { ts: stamp(), actor: 'Ana Robles', role: 'Oficial', action: `solicitó documentos por ${labels}` },
      ...e.history,
    ];
    return delay({
      id: `REQ-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
      expedienteId: id,
      channels,
      message: body.message ?? 'Solicitud de documentos de debida diligencia (Ley 23).',
      status: 'sent' as const,
      providerRefs: {},
      sentAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    });
  },

  // --- Batch sanctions screening (mass list validation) ---
  // Kept for structural parity with realApi (the real bulk-screen endpoint); the
  // Validación masiva page now matches names against the client base instead, so
  // this is no longer called by the UI. It still mirrors the backend stub screen.
  screenBatch: (items: { name: string; country?: string }[]) => {
    const results: ScreeningItemResult[] = items.map((it) => {
      const hit = screenName(it.name);
      if (!hit) return { name: it.name, status: 'clear', matches: [] };
      return {
        name: it.name,
        status: hit.tier,
        matches: [
          { listName: hit.listName, matchedName: hit.matchedName, score: hit.score, tier: hit.tier },
        ],
      };
    });
    return delay({
      provider: 'stub-watchlist (demo)',
      screenedAt: nowIso(),
      total: results.length,
      hits: results.filter((r) => r.status === 'hit').length,
      reviews: results.filter((r) => r.status === 'review').length,
      results,
    });
  },

  // --- Registro Público (consulta on-demand) ---
  // Demo: resuelve el folio contra un set de ejemplos FICTICIOS de forma
  // determinista (o sintetiza un resultado plausible). Delay más largo para
  // imitar la latencia real de abrir el folio en el sitio.
  rpConsulta: (input: RpConsultaInput): Promise<RpConsultaResult> =>
    delay(resolverConsultaDemo(input), 900),

  // --- Vigilancia Registral: watchlist + chequeo semanal ---
  rpWatchList: (): Promise<RpWatch[]> => delay(rpWatchlist.map((w) => ({ ...w }))),

  rpWatchAdd: (input: RpConsultaInput): Promise<RpWatch> => {
    const folio = input.folio.trim();
    const existing = rpWatchlist.find((w) => w.folio === folio && w.seccion === input.seccion);
    if (existing) return delay({ ...existing });
    const r = resolverConsultaDemo(input);
    const nowMs = Date.now();
    const w: RpWatch = {
      id: `RPW-${String(rpWatchSeq++).padStart(3, '0')}`,
      seccion: input.seccion,
      folio,
      denominacion: r.denominacion,
      addedAt: new Date(nowMs).toISOString(),
      lastCheckedAt: null,
      nextCheckAt: new Date(nowMs + 7 * 24 * 3600 * 1000).toISOString(),
      hallazgos: 0,
    };
    rpWatchlist = [w, ...rpWatchlist];
    return delay({ ...w });
  },

  rpWatchRemove: (id: string): Promise<RpWatch[]> => {
    rpWatchlist = rpWatchlist.filter((w) => w.id !== id);
    return delay(rpWatchlist.map((w) => ({ ...w })));
  },

  // Simula el chequeo semanal: revisa cada folio vigilado y, ante una condición
  // relevante (deriva de evaluarFolio — vigencia/fusión/prelación), genera una
  // alerta en la bandeja (tipo = «Vigilancia Registral · <Subsector>»). Deduplica
  // por folio+subsector para no repetir la misma alerta en cada corrida.
  rpRunWeeklyCheck: (): Promise<RpWeeklyCheckResult> => {
    const generadas: RpWeeklyCheckResult['alertas'] = [];
    const nowMs = Date.now();
    const nowIsoStr = new Date(nowMs).toISOString();
    const nextIso = new Date(nowMs + 7 * 24 * 3600 * 1000).toISOString();
    for (const w of rpWatchlist) {
      const r = resolverConsultaDemo({ seccion: w.seccion, folio: w.folio });
      for (const s of evaluarFolio(r)) {
        const key = `${w.folio}:${s.subsector}`;
        if (rpEmitted.has(key)) continue;
        rpEmitted.add(key);
        const alertId = `ALT-RP${String(rpAlertSeq++).padStart(3, '0')}`;
        const alert: Alert = {
          id: alertId,
          tipo: `${VIGILANCIA_TAB} · ${SUBSECTOR_LABEL[s.subsector]}`,
          cliente: `Folio ${w.folio} — ${w.denominacion}`,
          expedienteId: null,
          prioridad: s.prioridad,
          estado: 'Generada',
          asignada: null,
          descripcion: s.detalle,
          evidencia: [
            { label: 'Folio', value: w.folio },
            { label: 'Subsector', value: SUBSECTOR_LABEL[s.subsector] },
            { label: 'Origen', value: 'Vigilancia Registral · chequeo semanal' },
          ],
          abierta: nowIsoStr,
        };
        alerts = [alert, ...alerts];
        w.hallazgos += 1;
        generadas.push({
          folio: w.folio,
          denominacion: w.denominacion,
          subsector: s.subsector,
          prioridad: s.prioridad,
          detalle: s.detalle,
          alertId,
        });
      }
      w.lastCheckedAt = nowIsoStr;
      w.nextCheckAt = nextIso;
    }
    return delay({ revisados: rpWatchlist.length, nuevasAlertas: generadas.length, alertas: generadas }, 700);
  },

  // --- Vigilancia Registral: login RP + descarga por lote (demo) ---
  // Sin backend real: el login "acepta" cualquier credencial no vacía y el lote
  // devuelve un XLSX mínimo simulado. La demo NO scrapea el sitio.
  rpSalud: (): Promise<RpSalud> =>
    delay({
      ok: true,
      sesion_guardada: false,
      beta: {
        scrape_disponible: false,
        ocr_disponible: false,
        requiere_2captcha: true,
        nota: 'Demo: el scraping en vivo no está disponible en esta vista. Con el servicio conectado, el login usa sus credenciales del Registro Público y 2Captcha (reCAPTCHA v2).',
      },
    }),
  rpLogin: (input: RpLoginInput): Promise<{ ok: boolean; sesion_guardada?: boolean }> => {
    if (!input.usuario.trim() || !input.password.trim()) {
      return Promise.reject(new RequestError(401, 'REGISTRO_LOGIN_FALLIDO', 'Ingrese usuario y contraseña.'));
    }
    return delay({ ok: true, sesion_guardada: true }, 700);
  },
  // Barrer mi cartera (demo): recorre los expedientes juridicos, abre un folio de
  // ejemplo y levanta alertas cuando hay cambio (vigencia/fusión/prelación). Los
  // dos primeros juridicos usan folios de ejemplo con cambio (suspendido/fusión),
  // el tercero simula un error de lectura y el resto sale limpio (vigente). Los
  // clientes naturales cuentan como «sin folio». Determinista y sin backend real.
  rpSweep: (): Promise<RpSweepResult> => {
    const nowIso = new Date().toISOString();
    const cambioFolios = ['155123456', '1650944']; // suspendido, fusionado (RP_EJEMPLOS)
    const resultados: RpSweepRow[] = [];
    let cambios = 0;
    let errores = 0;
    let sinFolio = 0;
    let ji = 0; // índice de expediente juridico
    for (const e of expedientes) {
      if (e.persona !== 'legal') {
        sinFolio += 1; // los clientes naturales no tienen folio registral
        continue;
      }
      const folio = cambioFolios[ji] ?? e.folio ?? `25${880000 + ji}`;
      const esError = ji === 2; // el tercer juridico simula un folio ilegible
      ji += 1;
      if (esError) {
        errores += 1;
        resultados.push({
          expedienteId: e.id,
          nombre: e.name,
          folio,
          estado: 'Error',
          cambio: false,
          alerta: false,
          error: 'No se pudo abrir el folio en el Registro Público.',
        });
        continue;
      }
      const r = resolverConsultaDemo({ seccion: 'mercantil', folio });
      const signals = evaluarFolio(r);
      const cambio = signals.length > 0;
      let alerta = false;
      for (const s of signals) {
        const key = `sweep:${e.id}:${s.subsector}`;
        if (rpEmitted.has(key)) continue; // no repetir la misma alerta en otro barrido
        rpEmitted.add(key);
        alerta = true;
        const alertId = `ALT-RP${String(rpAlertSeq++).padStart(3, '0')}`;
        alerts = [
          {
            id: alertId,
            tipo: `${VIGILANCIA_TAB} · ${SUBSECTOR_LABEL[s.subsector]}`,
            cliente: e.name,
            expedienteId: e.id,
            prioridad: s.prioridad,
            estado: 'Generada',
            asignada: null,
            descripcion: s.detalle,
            evidencia: [
              { label: 'Folio', value: folio },
              { label: 'Cliente', value: e.name },
              { label: 'Origen', value: 'Vigilancia Registral · barrido de cartera' },
            ],
            abierta: nowIso,
          },
          ...alerts,
        ];
      }
      if (cambio) cambios += 1;
      resultados.push({
        expedienteId: e.id,
        nombre: e.name,
        folio,
        estado: VIGENCIA_LABEL[r.vigencia],
        cambio,
        alerta,
      });
    }
    return delay({ revisados: resultados.length, cambios, errores, sinFolio, resultados }, 900);
  },

  // --- AI assistant (scoped, data-backed actions) ---
  assistantExplainRisk: (expedienteId: string) => {
    const e = expedientes.find((x) => x.id === expedienteId);
    if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
    if (e.riesgo === 'sin' || e.factors.length === 0) {
      return delay({
        skill: 'explain_risk',
        source: 'fallback' as const,
        answer: `Aún no se ha calculado el riesgo de ${e.name}. Ejecute las validaciones para generar la evaluación.`,
      });
    }
    const lines = e.factors.map((f) => `• ${f.name} (${f.source}) — peso ${f.weight}`).join('\n');
    const rec =
      e.riesgo === 'alto'
        ? 'Aplicar Debida Diligencia Ampliada (EDD) y obtener aprobación de Gerencia antes de activar la relación.'
        : e.riesgo === 'medio'
          ? 'Aplicar debida diligencia reforzada y monitoreo periódico.'
          : 'Debida diligencia estándar; revisión según la cadencia asignada.';
    const pep = e.pep ? ' Incluye condición PEP en la estructura, lo que eleva el riesgo.' : '';
    return delay({
      skill: 'explain_risk',
      source: 'ai' as const,
      answer: `${e.name} presenta riesgo ${e.riesgo.toUpperCase()} (puntaje ${e.score}/100).${pep}\n\nFactores que pesan:\n${lines}\n\nRecomendación: ${rec}`,
    });
  },

  // --- AI assistant: free-text chat (lightweight intent router over demo data) ---
  // Read intents answer inline; write intents PROPOSE an action (returned in
  // `proposedActions`) that the panel surfaces as a confirm card — nothing is
  // executed until the user confirms via `assistantExecute`.
  assistantChat: (message: string, expedienteId?: string, _history?: Array<{ role: 'user' | 'assistant'; text: string }>, conversationId?: string): Promise<AssistantChatResponse> => {
    // Correlation-id parity with the real API (B3): echo the caller's
    // conversationId (or mint one when absent) and stamp a fresh turnId on every
    // response. Nothing is persisted — the demo doesn't keep a decision log.
    const convId = conversationId ?? crypto.randomUUID();
    const turnId = crypto.randomUUID();
    const withIds = (p: Promise<AssistantChatResponse>): Promise<AssistantChatResponse> =>
      p.then((r) => ({ ...r, conversationId: convId, turnId }));
    return withIds(((): Promise<AssistantChatResponse> => {
    const q = message.toLowerCase();
    const e = expedienteId ? expedientes.find((x) => x.id === expedienteId) : undefined;

    // ---- WRITE intents (propose, do not execute) ----

    // create_expediente — "crea/abre un expediente para <nombre>"
    if (/\b(crea|crear|abre|abrir|registra|registrar|añade|agrega|nuevo|nueva)\b/.test(q) && /(expediente|cliente|ficha)/.test(q)) {
      const name = trailingName(
        message,
        /(?:expediente|cliente|ficha)\s+(?:para|de|a|del|al)\s+(.+)/i
      ) || trailingName(message, /\b(?:para|de|a|al)\s+(.+)/i);
      if (!name) {
        return delay(plainChat('¿Para quién creo el expediente? Indíqueme el nombre del cliente.'));
      }
      const persona: Persona = /\b(s\.?a\.?|sociedad|empresa|corp|inc|fundaci[oó]n|holding|compa[ñn][ií]a)\b/i.test(name)
        ? 'legal'
        : 'natural';
      return delay(
        proposeChat(
          `Preparé una acción para abrir el expediente de «${name}». Revísela y confírmela para crearlo (queda en estado «Nuevo», sin validaciones).`,
          {
            tool: 'create_expediente',
            title: `Crear expediente — ${name}`,
            summary: `${persona === 'legal' ? 'Persona jurídica' : 'Persona natural'} · captura inicial (estado «Nuevo»).`,
            input: { nombre: name, persona },
          }
        )
      );
    }

    // request_documents — "solicita/pide documentos a <cliente>"
    if (/\b(solicita|solicitar|pide|pedir|env[ií]a|enviar|manda|mandar)\b/.test(q) && /(documento|documentos|papeles|requisito|requisitos)/.test(q)) {
      const name = trailingName(
        message,
        /(?:documentos?|papeles|requisitos?)\s+(?:a|al|para|de|del)\s+(.+)/i
      );
      const found = name ? matchClientes(name) : e ? [e] : [];
      if (found.length === 0) {
        return delay(
          plainChat(
            name
              ? `No encontré un cliente que coincida con «${name}». Abra su expediente o dígame el nombre exacto.`
              : 'Dígame a qué cliente le solicito los documentos (por nombre) o abra su expediente.'
          )
        );
      }
      if (found.length > 1) {
        const list = found.slice(0, 5).map((x) => `• ${x.name}`).join('\n');
        return delay(
          plainChat(`Encontré varios clientes que coinciden con «${name}». ¿A cuál se refiere?\n${list}`)
        );
      }
      const target = found[0]!;
      return delay(
        proposeChat(
          `Preparé una solicitud de documentos para «${target.name}». Revísela y confírmela para enviarla.`,
          {
            tool: 'request_documents',
            title: `Solicitar documentos — ${target.name}`,
            summary: 'Envío por WhatsApp · el expediente pasa a «Docs Pendientes».',
            input: { expedienteId: target.id, channels: ['whatsapp'] },
          }
        )
      );
    }

    // update_alert — "descarta/cierra/resuelve/asigna la alerta (de <cliente>)"
    if (/\b(descarta|descartar|cierra|cerrar|resuelve|resolver|asigna|asignar|escala|escalar)\b/.test(q) && /(alerta|alertas)/.test(q)) {
      const asignar = /\b(asigna|asignar)\b/.test(q);
      const escalar = /\b(escala|escalar)\b/.test(q);
      const name = trailingName(message, /alertas?\s+(?:de|del|para|a|al)\s+(.+)/i);
      const found = name
        ? matchAlertas(name)
        : e
          ? alerts.filter((a) => a.expedienteId === e.id)
          : [];
      if (found.length === 0) {
        return delay(
          plainChat(
            name
              ? `No encontré una alerta que coincida con «${name}».`
              : 'Dígame de qué cliente es la alerta o abra su expediente.'
          )
        );
      }
      if (found.length > 1) {
        const list = found.slice(0, 5).map((x) => `• ${x.cliente} — ${x.tipo}`).join('\n');
        return delay(plainChat(`Encontré varias alertas que coinciden. ¿Cuál actualizo?\n${list}`));
      }
      const target = found[0]!;
      const estado = asignar ? 'Asignada' : escalar ? 'Escalada → Gerencia' : 'Resuelta';
      const verbo = asignar ? 'asignar' : escalar ? 'escalar' : 'descartar/resolver';
      return delay(
        proposeChat(
          `Preparé una acción para ${verbo} la alerta de «${target.cliente}». Revísela y confírmela.`,
          {
            tool: 'update_alert',
            title: `Actualizar alerta — ${target.cliente}`,
            summary: `${target.tipo} · nuevo estado «${estado}».`,
            input: {
              alertId: target.id,
              estado,
              ...(asignar ? { assignedTo: me.fullName } : {}),
              ...(!asignar && !escalar
                ? { nota: 'Revisada y documentada desde el Asistente IA (demo).' }
                : {}),
            },
          }
        )
      );
    }

    // run_validation — "ejecuta/corre screening|valida a <cliente>"
    const wantsRun =
      (/\b(ejecuta|ejecutar|corre|correr|realiza|realizar|haz|hacer|lanza|lanzar)\b/.test(q) &&
        /(screening|validaci|valida|cotej|listas?|sancion)/.test(q)) ||
      /\bvalida[r]?\s+a(l)?\s+/.test(q);
    if (wantsRun) {
      const rawName =
        trailingName(
          message,
          /(?:screening|validaci\w+|valida[r]?|cotej\w+|listas?|sanciones?)\s+(?:a|al|de|del|para|sobre)\s+(.+)/i
        ) || trailingName(message, /\b(?:a|al|sobre)\s+(.+)/i);
      // Guard: "ejecuta la validación de sanciones" names the validation TYPE,
      // not a client. If the extracted term is a validation-type keyword (and
      // matches no client), drop it so the request falls back to the expediente
      // currently in context instead of screening a phantom «sanciones».
      const isValidationTypeWord = /^(sanciones?|screening|listas?|pep|identidad|svi|credito|cr[eé]dito|apc|domicilio|riesgo|kyc|validaci[oó]n(es)?)$/i.test(
        normName(rawName)
      );
      const name = isValidationTypeWord && matchClientes(rawName).length === 0 ? '' : rawName;
      const found = name ? matchClientes(name) : e ? [e] : [];
      if (found.length === 1) {
        const target = found[0]!;
        return delay(
          proposeChat(
            `Preparé un screening de listas para «${target.name}». Revíselo y confírmelo para ejecutarlo.`,
            {
              tool: 'run_validation',
              title: `Ejecutar screening — ${target.name}`,
              summary: 'Cotejo contra listas OFAC · ONU · UE · Reino Unido · Canadá.',
              input: { expedienteId: target.id, key: 'sanctions' },
            }
          )
        );
      }
      if (found.length > 1) {
        const list = found.slice(0, 5).map((x) => `• ${x.name}`).join('\n');
        return delay(
          plainChat(`Encontré varios clientes que coinciden con «${name}». ¿A cuál le corro el screening?\n${list}`)
        );
      }
      // No client resolved — fall through to the read-only name screen below.
    }

    // ---- READ intents (answer inline) ----

    // parse_rp_document — pasted Registro Público content or an explicit
    // "lee/analiza este documento" ask. Guarded by HARD document markers on the
    // ORIGINAL message ([TAB:...], "Folio Real", "Código de Ubicación", asiento
    // lines) — a mere mention of «Registro Público» in a question must NOT land
    // here (e.g. preguntas sobre el barrido/vigilancia caen a los intents de abajo).
    {
      const rpDocMarker =
        /\[TAB:/.test(message) ||
        /Folio Real/i.test(message) ||
        /C[oó]digo de Ubicaci[oó]n/i.test(message) ||
        /Derechos \/ Actos/i.test(message) ||
        /N[ºo°]? de Entrada/i.test(message);
      const rpAsk =
        /\b(lee|leer|analiza|analizar|interpreta|interpretar)\b/.test(q) &&
        /documento/.test(q) &&
        /registro p[uú]blico/i.test(q);
      if (rpDocMarker || rpAsk) {
        const parsed = parseRpDocumento(message);
        const toolResults: AssistantToolResult[] = [{ tool: 'parse_rp_document', data: parsed }];
        const nada =
          parsed.denominacion === undefined &&
          parsed.folio === undefined &&
          parsed.miembros.length === 0 &&
          parsed.partes.length === 0;
        if (nada) {
          const adv = parsed.advertencias.map((a) => `• ${a}`).join('\n');
          return delay({
            toolsUsed: ['parse_rp_document'],
            answer: `No pude interpretar el documento: no encontré folio, denominación ni partes en el texto. Pega el contenido completo tal como lo descargaste del Registro Público.${adv ? `\n\n${adv}` : ''}`,
            toolResults,
          });
        }
        const lines: string[] = [];
        const titular =
          parsed.denominacion ??
          parsed.partes.find((p) => /owner|propietari/i.test(p.rol))?.nombre ??
          parsed.partes[0]?.nombre;
        if (titular) lines.push(`Titular: ${titular}`);
        if (parsed.folio) lines.push(`Folio: ${parsed.folio}`);
        if (parsed.estado) lines.push(`Estado: ${parsed.estado}${parsed.estadoInferido ? ' (inferido)' : ''}`);
        if (parsed.miembros.length > 0) {
          const cargos = parsed.miembros.slice(0, 3).map((m) => `${m.cargo}: ${m.nombre}`).join(' · ');
          lines.push(
            `Junta directiva: ${parsed.miembros.length} miembro${parsed.miembros.length === 1 ? '' : 's'} (${cargos}${parsed.miembros.length > 3 ? ' …' : ''}).`
          );
        }
        const vigentes = parsed.gravamenes.filter((g) => g.vigente);
        lines.push(
          vigentes.length
            ? `Gravámenes vigentes:\n${vigentes.map((g) => `• ${g.tipo} — ${g.detalle}`).join('\n')}`
            : 'Sin gravámenes vigentes.'
        );
        for (const a of parsed.alertas) lines.push(`⚠ ${a}`);
        return delay({ toolsUsed: ['parse_rp_document'], answer: lines.join('\n'), toolResults });
      }
    }

    // list_capabilities — "¿qué puedes hacer? / ¿en qué me ayudas? / opciones".
    // A greeting-help / capabilities question → a one-line intro + the demo
    // catalog as interactive capability cards (parity with the real tool). Sits
    // FIRST so a bare "opciones/funciones/capacidades" resolves here, not below.
    // `cap` is accent-stripped so "qué/cuáles/qué ofreces" match without accents.
    const cap = q.normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (
      /\bque\s+puedes?\s+hacer\b/.test(cap) ||
      /\ben\s+que\s+(me\s+)?(puedes\s+)?ayud/.test(cap) ||
      /\bcomo\s+(me\s+)?puedes\s+ayudar\b/.test(cap) ||
      /\b(cuales?|que)\s+son\s+tus\s+(funciones|opciones|capacidades)\b/.test(cap) ||
      /\bpara\s+que\s+sirves\b/.test(cap) ||
      /\bque\s+(me\s+)?ofreces\b/.test(cap) ||
      /\b(opciones|capacidades|funciones)\b/.test(cap)
    ) {
      // Reuse the demo catalog (excluding any list_capabilities entry), copied so
      // callers can't mutate the seed. `data.tools` powers the inline cards.
      const catalog = DEMO_ASSISTANT_TOOLS.filter((t) => t.name !== 'list_capabilities').map(
        (t) => ({ ...t })
      );
      const toolResults: AssistantToolResult[] = [
        { tool: 'list_capabilities', data: { tools: catalog } },
      ];
      return delay({
        toolsUsed: ['list_capabilities'],
        answer: 'Esto es lo que puedo hacer:',
        toolResults,
      });
    }

    // Matrix questions — DETERMINISTIC: composed verbatim from the documented
    // reference (mirrors the real explain_risk_matrix tool; no model involved).
    // Coverage: overview/cálculo, umbrales/DDC, overrides, país concreto, sector,
    // sub-factor, familia — the assistant can explain EVERYTHING about the matrix.
    const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const nq = fold(q);
    const matrixCountry = RISK_COUNTRY_RISK_CATALOG.find(
      (c) => c.key !== 'pa' && new RegExp(`(^|[^a-z])${fold(c.label).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`).test(nq)
    );
    if (
      matrixCountry ||
      /(matriz|peso|ponderaci|umbral|nivel|ddc|debida diligencia|edd|override|sub-?factor|sub-?indicador|listas internacionales|sector|recomiend|calibraci|puntaje|score|como funciona|cómo funciona|simulador|pais|país)/.test(q)
    ) {
      const rec = RECOMMENDED_RISK_MATRIX;
      const recLine = `Recomendación documentada: Cliente ${rec.weights.cliente}% · Jurisdicción ${rec.weights.jurisdiccion}% · Producto ${rec.weights.producto}% · Canal ${rec.weights.canal}% (rangos Wolfsberg App. I conciliados al Acuerdo 1-2026 Art. 13). Umbrales: medio 40 / alto 70.`;
      const regla =
        'Regla: los pesos son calibración del sujeto obligado — la ley fija los factores, no los porcentajes — y cada nivel debe sumar 100% (Guía SSNF). Los overrides (sanciones, PEP, FATF negra) solo elevan el nivel.';

      // Concrete country → its band, fundamento, and how it enters the score.
      if (matrixCountry) {
        const ovr = matrixCountry.override
          ? '\n\nAdemás dispara el OVERRIDE de país (FATF lista negra / sanciones integrales): fuerza nivel ALTO y bloquea el alta automática, sin importar el puntaje ponderado.'
          : '';
        return delay({
          toolsUsed: ['explain_risk_matrix'],
          answer: `${matrixCountry.label}: banda ${matrixCountry.band.toUpperCase()} en los sub-factores de geografía (nacionalidad, origen/destino de fondos, domicilio).\n\n¿Por qué? ${matrixCountry.fundamento}.${ovr}\n\nLa banda por país es calibración de referencia (corte jun 2026) — verifique la lista GAFI/UE vigente antes de decidir.\n\n${regla}`,
        });
      }

      // Overview — "cómo funciona / cómo se calcula / explícame la matriz".
      if (/(como funciona|cómo funciona|como se calcula|cómo se calcula|explica|que es la matriz|qué es la matriz|puntaje|score)/.test(q)) {
        return delay({
          toolsUsed: ['explain_risk_matrix'],
          answer:
            `La matriz pondera 4 factores (Acuerdo 1-2026 Art. 13): Cliente, Jurisdicción/Geografía, Producto y Canal/Transaccionalidad. Cada factor se puntúa 0–100 por sus sub-factores (el oficial elige situaciones concretas y la banda bajo/medio/alto se deriva del catálogo documentado) y el puntaje final es el promedio ponderado.\n\n` +
            `El puntaje cae en un nivel según los umbrales: bajo (< medio), medio y alto — y el nivel define la debida diligencia: bajo → DDC simplificada (revisión 36 m); medio → DDC básica (24 m); alto → DDC ampliada/EDD (12 m).\n\n` +
            `Los indicadores de override (sanciones, PEP, FATF negra, antecedentes) NO se promedian: elevan el nivel por regla, nunca lo bajan. Sanciones y FATF negra además bloquean el alta.\n\n${recLine}\n\n${regla}`,
        });
      }

      // Umbrales / niveles / DDC.
      if (/(umbral|nivel|ddc|debida diligencia|edd|revisi)/.test(q) && !/(sub-?factor|sector)/.test(q)) {
        return delay({
          toolsUsed: ['explain_risk_matrix'],
          answer: `El puntaje compuesto (0–100) se clasifica por los umbrales configurados (recomendados: medio desde 40, alto desde 70):\n\n• BAJO → DDC simplificada · revisión cada 36 meses.\n• MEDIO → DDC básica · revisión cada 24 meses.\n• ALTO → DDC ampliada (EDD) · revisión cada 12 meses + aprobación de instancia superior.\n\nUn override (sanciones, PEP, FATF negra, antecedente firme) puede forzar el nivel hacia arriba aunque el puntaje sea bajo — nunca hacia abajo.\n\n${regla}`,
        });
      }

      // Overrides / indicadores.
      if (/(override|sancion|sanción|pep|bloquea|congelamiento|antecedente firme)/.test(q)) {
        const lines = RISK_INDICATOR_REFERENCE.map((i) => `• ${i.label}: ${i.efecto} (${i.fundamento}).`).join('\n');
        return delay({
          toolsUsed: ['explain_risk_matrix'],
          answer: `Los indicadores de override están FUERA de la aritmética ponderada: elevan el nivel por regla (piso), nunca lo bajan.\n\n${lines}\n\nAlto/crítico gatilla EDD y revisión humana — nunca un rechazo automático.\n\n${regla}`,
        });
      }

      // Match a sector by any stemmed word of its label (≥5 chars, plural-tolerant),
      // so "fiduciaria", "casinos" or "contador" all resolve their preset.
      const sector = SECTOR_RISK_PRESETS.find(
        (p) =>
          p.key !== 'general' &&
          p.label
            .toLowerCase()
            .split(/[^a-záéíóúñ]+/)
            .filter((w) => w.length >= 5)
            .map((w) => w.replace(/es$/, '').replace(/s$/, ''))
            .some((k) => q.includes(k))
      );
      if (sector) {
        return delay({
          toolsUsed: ['explain_risk_matrix'],
          answer: `${sector.label} (${sector.supervisor}): Cliente ${sector.weights.cliente}% · Jurisdicción ${sector.weights.jurisdiccion}% · Producto ${sector.weights.producto}% · Canal ${sector.weights.canal}%.\n\n¿Por qué? ${sector.fundamento}\n\n${regla}`,
        });
      }
      for (const fam of RISK_FACTOR_REFERENCE) {
        const sf = fam.subfactors.find(
          (s) => q.includes(s.key.replace(/_/g, ' ')) || q.includes(s.label.toLowerCase().split(' ')[0]!)
        );
        if (sf) {
          return delay({
            toolsUsed: ['explain_risk_matrix'],
            answer: `${sf.label} (familia ${fam.label}): ${sf.explicacion}\n\nBandas: bajo = ${sf.bands.bajo}; medio = ${sf.bands.medio}; alto = ${sf.bands.alto}.\n\nFundamento: ${sf.fundamento}.\n\n${regla}`,
          });
        }
      }
      const fam = RISK_FACTOR_REFERENCE.find((f) => q.includes(f.label.toLowerCase()));
      if (fam) {
        return delay({
          toolsUsed: ['explain_risk_matrix'],
          answer: `${fam.label}${fam.aka ? ` (${fam.aka})` : ''}: ${fam.explicacion}\n\nFundamento: ${fam.fundamento}. Peso recomendado: ${rec.weights[fam.key]}%.\n\n${recLine}\n\n${regla}`,
        });
      }
      return delay({
        toolsUsed: ['explain_risk_matrix'],
        answer: `${recLine}\n\n${regla}\n\nPuede preguntar: cómo se calcula el puntaje, los umbrales y la DDC, los overrides, un país concreto (Irán, Venezuela, Kazajistán…), un sub-indicador (profesión, estructura, listas, monto…) o un sector (banca, fiduciarias, abogados, casinos…).`,
      });
    }

    // Ley 23 compliance/prevention obligations — DETERMINISTIC from the shared
    // reference (mirrors the real explain_compliance_obligations tool; no model).
    // Resolve the topic to an eje FIRST so follow-ups that name a pillar
    // ("háblame del congelamiento preventivo", "y la capacitación") are answered,
    // not dropped to the generic fallback. Demo has no conversation memory, so a
    // follow-up must name its topic; the real backend carries the chat history.
    const ejeByKey = (Object.keys(LEY23_EJE_LABELS) as Ley23Eje[]).find((k) => nq.includes(k));
    const ejeBySyn = (
      [
        [/congelamiento|consejo de seguridad|lista(s)? de la onu|descongel/, 'congelamiento'],
        [/capacitaci|capacit|conocimiento del empleado|\bkye\b|idoneidad|empleado|personal/, 'personal'],
        [/reporte|\bros\b|\brte\b|operaci[oó]n sospechosa|en efectivo|\buaf\b|tipping|confidencialidad/, 'reportes'],
        [/registro|conservaci|resguard|transferencia|retenci[oó]n/, 'registros'],
        [/seguimiento|monitoreo|examen especial|inusual/, 'monitoreo'],
        [/manual de prevenci|enfoque basado en riesgo|autoevaluaci/, 'manual'],
        [/estructura de cumplimiento|enlace|oficial de cumplimiento|control interno/, 'estructura'],
      ] as [RegExp, Ley23Eje][]
    ).find(([re]) => re.test(nq))?.[1];
    const eje = ejeByKey ?? ejeBySyn;
    if (
      eje ||
      /(ley\s*23|d\.?e\.?\s*35|obligaci|cumplimiento|manual de prevenci|operaci[oó]n sospechosa|oficial de cumplimiento)/.test(
        nq
      )
    ) {
      // Sujeto filter (financiero / no financiero), combined with any eje narrowing.
      // 'todos' applies to everyone, so it survives a sujeto filter. Order matters:
      // test "no financier" BEFORE "financier" (the latter is a substring).
      const sujetoFilter = /no financier/.test(nq)
        ? ('no financieros' as const)
        : /financier/.test(nq)
          ? ('financieros' as const)
          : undefined;
      const rows = LEY23_OBLIGATION_REFERENCE.filter(
        (o) =>
          (eje ? o.eje === eje : true) &&
          (sujetoFilter ? ley23SujetoAplica(o.sujeto, sujetoFilter) : true)
      );
      const sujetoNote = sujetoFilter
        ? ` para sujetos ${sujetoFilter === 'no financieros' ? 'NO financieros' : 'financieros'}`
        : '';
      const nota =
        '_Fundamento: Ley 23 de 2015 y D.E. 35 de 2022. Citas verificadas contra la fuente; no sustituye asesoría legal._';
      // Group by eje (canonical order) and emit real markdown — a bold section
      // heading + a bullet list per eje — so the answer renders as scannable
      // sections through <ChatMarkdown>, not one run-on paragraph. (A single "\n"
      // is a soft wrap in Markdown; "- " lines are what become a real <ul>.)
      const ejeOrder: Ley23Eje[] = [
        'estructura',
        'manual',
        'reportes',
        'personal',
        'monitoreo',
        'registros',
        'congelamiento',
      ];

      // Plazos / umbrales → the key deadlines list (mirrors the tool's `plazos`).
      if (/(plazo|plazos|umbral|cu[aá]ndo|d[ií]as|fecha l[ií]mite|vencimiento)/.test(nq)) {
        const list = LEY23_KEY_DEADLINES.map(
          (d) => `- **${d.tema}**: ${d.valor} _(${d.articulo})_`
        ).join('\n');
        return delay({
          toolsUsed: ['explain_compliance_obligations'],
          answer: `Plazos y umbrales clave bajo la Ley 23/2015 y el D.E. 35/2022:\n\n${list}\n\n${nota}`,
        });
      }

      // Resumen / conteo → per-eje counts + total (mirrors the tool's `resumen`).
      if (/(resumen|res[uú]men|cu[aá]ntas|cuantas|panorama|cu[aá]les ejes)/.test(nq)) {
        const perEje = ejeOrder
          .map((k) => ({ k, n: rows.filter((o) => o.eje === k).length }))
          .filter((g) => g.n > 0);
        const list = perEje.map((g) => `- **${LEY23_EJE_LABELS[g.k]}**: ${g.n}`).join('\n');
        return delay({
          toolsUsed: ['explain_compliance_obligations'],
          answer: `**${rows.length} obligaciones en ${perEje.length} ejes**${sujetoNote} (Ley 23/2015 y D.E. 35/2022):\n\n${list}\n\n${nota}`,
        });
      }

      // Narrowed to ONE eje ("háblame más") → append the verbatim law quote for depth.
      const detail = eje != null;
      const body = ejeOrder
        .map((ej) => ({ ej, items: rows.filter((o) => o.eje === ej) }))
        .filter((g) => g.items.length > 0)
        .map(
          (g) =>
            `**${LEY23_EJE_LABELS[g.ej]}**\n` +
            g.items
              .map((o) =>
                detail
                  ? `- ${o.obligacion} _(${o.norma}, ${o.articulo})_ — «${o.extracto}»`
                  : `- ${o.obligacion} _(${o.norma}, ${o.articulo})_`
              )
              .join('\n')
        )
        .join('\n\n');
      // Surface the documented gap (Oficial de Cumplimiento) when the user asks for it.
      const gap = /oficial de cumplimiento/.test(nq)
        ? '\n\n' + LEY23_OBLIGATION_GAPS.map((h) => `**${h.tema}** — ${h.nota}`).join('\n\n')
        : '';
      const heading = eje
        ? `Sobre **${LEY23_EJE_LABELS[eje]}**${sujetoNote} (Ley 23/2015 y D.E. 35/2022):`
        : `Tus obligaciones de función de cumplimiento y prevención${sujetoNote} bajo la Ley 23/2015 y el D.E. 35/2022:`;
      return delay({
        toolsUsed: ['explain_compliance_obligations'],
        answer: `${heading}\n\n${body}${gap}\n\n${nota}`,
      });
    }

    if (/(riesgo|por que|porque|nivel)/.test(q) && e) {
      const factors = e.factors.map((f) => {
        const ev = DEMO_PESO_EVIDENCE[f.weight];
        return { label: `${f.name} (${ev.weightPct}%)`, value: `${ev.band} · ${ev.pts} pts` };
      });
      const lines = factors.map((f) => `• ${f.label} — ${f.value}`).join('\n');
      const toolResults: AssistantToolResult[] = [
        {
          tool: 'explain_client_risk',
          data: {
            nombre: e.name,
            riesgo: e.riesgo,
            score: e.score,
            pep: e.pep,
            hasRiskRun: e.riesgo !== 'sin' && e.factors.length > 0,
            factors,
          },
        },
      ];
      const topFactor = e.factors[0];
      return delay({
        toolsUsed: ['explain_client_risk'],
        answer:
          `${e.name} está clasificado en **riesgo ${e.riesgo.toUpperCase()} (${e.score}/100)**, calculado con la matriz vigente de su entidad.\n\n` +
          `${topFactor ? `Lo que más pesa es **${topFactor.name.toLowerCase()}**; el desglose completo:\n` : 'Desglose de factores:\n'}${lines}\n\n` +
          (e.pep
            ? `Además, la condición **PEP** fija un piso de riesgo ALTO por override: exige debida diligencia ampliada y aprobación de gerencia — nunca un rechazo automático.\n\n`
            : '') +
          `El nivel final lo asigna su analista; yo se lo fundamento con los pesos y umbrales que ustedes mismos calibraron.`,
        toolResults,
      });
    }
    if (/(documento|faltan|falta|vence)/.test(q) && e) {
      const pend = e.docs.filter((d) => d.estado !== 'Vigente' && d.estado !== 'Aceptado');
      // Requested-but-not-uploaded docs read as "missing"; the rest are present-with-issue.
      const missing = pend.filter((d) => d.estado === 'Solicitado').map((d) => d.name);
      const expiring = pend
        .filter((d) => d.estado !== 'Solicitado')
        .map((d) => ({ category: d.name, estado: d.estado }));
      const present = e.docs
        .filter((d) => d.estado === 'Vigente' || d.estado === 'Aceptado')
        .map((d) => ({ category: d.name, estado: d.estado }));
      const list = pend.length
        ? pend.map((d) => `• ${d.name} — ${d.estado}`).join('\n')
        : 'No hay documentos pendientes registrados.';
      const toolResults: AssistantToolResult[] = [
        {
          tool: 'list_missing_documents',
          data: { nombre: e.name, required: e.docs.map((d) => d.name), present, missing, expiring },
        },
      ];
      return delay({
        toolsUsed: ['list_missing_documents'],
        answer: `Documentos a atender de ${e.name}:\n${list}`,
        toolResults,
      });
    }
    if (/(resumen|c[oó]mo vamos|cu[aá]ntos|panorama)/.test(q)) {
      const tally = (keys: string[]): Map<string, number> => {
        const m = new Map<string, number>();
        for (const k of keys) m.set(k, (m.get(k) ?? 0) + 1);
        return m;
      };
      const byEstadoMap = tally(expedientes.map((x) => x.estado));
      const byRiesgoMap = tally(expedientes.map((x) => x.riesgo));
      const byEstado = [...byEstadoMap].map(([estado, count]) => ({ estado, count }));
      const byRiesgo = [...byRiesgoMap].map(([riesgo, count]) => ({ riesgo, count }));
      const openAlerts = alerts.filter(isOpenAlert).length;
      const alto = byRiesgoMap.get('alto') ?? 0;
      const toolResults: AssistantToolResult[] = [
        {
          tool: 'get_dashboard_summary',
          data: { totalExpedientes: expedientes.length, byEstado, byRiesgo, openAlerts },
        },
      ];
      const bajo = byRiesgoMap.get('bajo') ?? 0;
      const medio = byRiesgoMap.get('medio') ?? 0;
      const altaAlerts = alerts.filter((a) => isOpenAlert(a) && a.prioridad === 'Alta');
      const focusLines = alerts
        .filter(isOpenAlert)
        .slice(0, 3)
        .map((a) => `- **${a.tipo}** — ${a.cliente} (prioridad ${a.prioridad.toLowerCase()})`);
      return delay({
        toolsUsed: ['get_dashboard_summary'],
        answer:
          `Con gusto. Revisé su cartera completa: **${expedientes.length} expedientes** — ${bajo} en riesgo bajo, ${medio} en medio y **${alto} en alto**. En conjunto se ve sana; la concentración está donde debe estar.\n\n` +
          `Lo que sí requiere su atención hoy son las **${openAlerts} alertas abiertas**${altaAlerts.length ? ` (${altaAlerts.length} de prioridad alta)` : ''}:\n${focusLines.join('\n')}\n\n` +
          `¿Quiere que abra alguna de estas alertas, o le preparo una acción — por ejemplo, solicitar los documentos que están por vencer?`,
        toolResults,
      });
    }
    if (/(alertas|casos)/.test(q)) {
      const open = alerts.filter(isOpenAlert);
      const rows = open.slice(0, 8);
      const toolResults: AssistantToolResult[] = [
        {
          tool: 'list_alerts',
          data: {
            count: open.length,
            alerts: rows.map((a) => ({
              id: a.id,
              expedienteId: a.expedienteId,
              alertType: a.tipo,
              title: `${a.tipo} — ${a.cliente}`,
              priority: a.prioridad,
              estado: a.estado,
              openedAt: a.abierta ?? todayIso(),
            })),
          },
        },
      ];
      const lines = rows.map((a) => `• ${a.tipo} — ${a.cliente} (${a.prioridad})`);
      return delay({
        toolsUsed: ['list_alerts'],
        answer: open.length
          ? `Tiene ${open.length} alerta${open.length === 1 ? '' : 's'} abierta${open.length === 1 ? '' : 's'}:\n${lines.join('\n')}`
          : 'No hay alertas abiertas en este momento.',
        toolResults,
      });
    }
    if (/(valida|lista|sancion|cotej|screening)/.test(q)) {
      const name =
        message
          .replace(/.*?(validar?|cotej[a-z]*|buscar?|screening|cotejo)[^a-záéíóúñ]*(de\s+|a\s+|al\s+)?/i, '')
          .replace(/\s+(en|contra)\s+(las?\s+)?listas?( de sanciones)?\s*\.?\s*$/i, '')
          .trim() || message.trim();
      const hit = screenName(name);
      const status: 'hit' | 'review' | 'clear' = hit ? hit.tier : 'clear';
      const toolResults: AssistantToolResult[] = [
        {
          tool: 'screen_name',
          data: {
            name,
            status,
            matches: hit
              ? [{ listName: hit.listName, matchedName: hit.matchedName, score: hit.score, tier: hit.tier }]
              : [],
          },
        },
      ];
      return delay({
        toolsUsed: ['screen_name'],
        answer: hit
          ? hit.tier === 'hit'
            ? `Cotejé «${name}» contra **OFAC (SDN), ONU, UE, Reino Unido y Canadá**.\n\n**Coincidencia bloqueante** en ${hit.listName} — «${hit.matchedName}», similitud ${hit.score}%. Mi recomendación: congele la emisión y abra un caso para adjudicar identidad (cédula y fecha de nacimiento contra la entrada listada) antes de continuar, como exige la Ley 23.`
            : `Cotejé «${name}» contra **OFAC (SDN), ONU, UE, Reino Unido y Canadá**.\n\n**Posible coincidencia** en ${hit.listName} — «${hit.matchedName}», similitud ${hit.score}%. No es concluyente: verifique fecha de nacimiento y documento para descartar una homonimia antes de decidir. Mientras tanto, trátelo como revisión, no como bloqueo.`
          : `Cotejé «${name}» contra **OFAC (SDN), ONU, UE, Reino Unido y Canadá**.\n\n**Sin coincidencias.** Puede continuar con la debida diligencia normal — y el re-screening nocturno lo seguirá vigilando todos los días.`,
        toolResults,
      });
    }
    // list_clients — "muéstrame los clientes recientes / ver mis clientes /
    // lista de clientes / la cartera". Sits BEFORE find_client so a plural,
    // non-specific "clientes" request returns the whole book (sorted by fecha de
    // inicio, más reciente primero) instead of mis-routing to an empty search.
    if (/\bclientes\b/.test(cap) && /(recient|todos|toda|lista|listar|muestr|ver|cartera|cuales|cuantos|mis|activos|libro)/.test(cap)) {
      const sorted = [...expedientes].sort((a, b) =>
        a.started < b.started ? 1 : a.started > b.started ? -1 : 0
      );
      const rows = sorted.slice(0, 12);
      const toolResults: AssistantToolResult[] = [
        {
          tool: 'list_clients',
          data: {
            count: expedientes.length,
            clients: rows.map((x) => ({
              expedienteId: x.id,
              nombre: x.name,
              estado: x.estado,
              riesgo: x.riesgo,
              score: x.score,
              persona: x.persona,
            })),
          },
        },
      ];
      const altoRows = sorted.filter((x) => x.riesgo === 'alto');
      return delay({
        toolsUsed: ['list_clients'],
        answer:
          `Aquí tiene su cartera — **${expedientes.length} clientes**, del más reciente al más antiguo. Toque cualquiera para abrir su expediente.\n\n` +
          (altoRows.length
            ? `Un apunte antes de que siga: **${altoRows.length} operan en riesgo alto** (${altoRows
                .slice(0, 3)
                .map((x) => x.name)
                .join(', ')}) — son los de revisión más frecuente y donde conviene empezar.`
            : `Ninguno opera hoy en riesgo alto.`),
        toolResults,
      });
    }
    if (/(busca|encuentra|cliente|expediente)/.test(q)) {
      const term = q
        .replace(/.*?(busca|encuentra|cliente|expediente)/g, '')
        .replace(/\b(a|al|el|la|de|un|una)\b/g, '')
        .trim();
      const found = expedientes
        .filter((x) => x.name.toLowerCase().includes(term) || term.length < 2)
        .slice(0, 5);
      const toolResults: AssistantToolResult[] = [
        {
          tool: 'find_client',
          data: {
            count: found.length,
            matches: found.map((x) => ({
              expedienteId: x.id,
              nombre: x.name,
              estado: x.estado,
              riesgo: x.riesgo,
              score: x.score,
              persona: x.persona,
            })),
          },
        },
      ];
      const lines = found.map((x) => `• ${x.name} — ${x.estado} · riesgo ${x.riesgo}`);
      return delay({
        toolsUsed: ['find_client'],
        answer: lines.length ? `Clientes encontrados:\n${lines.join('\n')}` : 'No encontré clientes que coincidan.',
        toolResults,
      });
    }
    return delay({
      toolsUsed: [],
      answer:
        'Puedo ayudarle con: resumir el riesgo de un cliente, listar documentos faltantes, validar un nombre en listas, o buscar un cliente. También puedo preparar acciones (solicitar documentos, correr screening, actualizar una alerta o abrir un expediente) para que las confirme. Abra un expediente para preguntas sobre «este cliente».',
    });
    })());
  },

  // --- AI assistant: confirm + execute a proposed write action ---
  // Mirrors the real POST /assistant/execute: switch on the (write) tool name,
  // mutate the in-memory store so the UI visibly updates, and return a faithful
  // envelope (incl. `entity` so the panel can deep-link into the app).
  // `_opts` (conversationId / turnId / edited) mirrors the real signature so the
  // `demoApi satisfies typeof realApi` parity holds; the demo persists no decision
  // log, so the correlation ids are accepted and ignored.
  assistantExecute: (tool: string, input: Record<string, unknown>, _opts?: { conversationId?: string | undefined; turnId?: string | undefined; edited?: boolean | undefined }): Promise<AssistantExecuteResponse> => {
    switch (tool) {
      case 'create_expediente': {
        const persona: Persona = asStr(input.persona) === 'legal' ? 'legal' : 'natural';
        const name = asStr(input.nombre) ?? asStr(input.name) ?? 'Cliente sin nombre';
        const e = buildExpediente({
          persona,
          name,
          docId: asStr(input.docId) ?? 'Por definir',
          sector: asStr(input.sector) ?? 'Por definir',
          kind: asStr(input.kind) ?? (persona === 'legal' ? 'Persona Jurídica' : 'Persona Natural'),
          ...(asStr(input.email) ? { email: asStr(input.email)! } : {}),
          ...(asStr(input.phone) ? { phone: asStr(input.phone)! } : {}),
        });
        expedientes = [e, ...expedientes];
        return delay(execResult(tool, { ...e }, { type: 'expediente', id: e.id }));
      }
      case 'update_expediente': {
        const id = asStr(input.expedienteId) ?? asStr(input.id) ?? '';
        const e = expedientes.find((x) => x.id === id);
        if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
        const estado = asStr(input.estado) as EstadoExpediente | undefined;
        const assignedTo = asStr(input.assignedTo);
        if (estado) e.estado = estado;
        if (assignedTo) e.analyst = assignedTo;
        e.history = [
          {
            ts: stamp(),
            actor: me.fullName,
            role: 'Asistente IA',
            action: estado ? `actualizó el estado a ${estado}` : 'actualizó el expediente',
          },
          ...e.history,
        ];
        return delay(execResult(tool, { ...e }, { type: 'expediente', id: e.id }));
      }
      case 'request_documents': {
        const id = asStr(input.expedienteId) ?? asStr(input.id) ?? '';
        const e = expedientes.find((x) => x.id === id);
        if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
        const channels = Array.isArray(input.channels) && input.channels.length > 0
          ? (input.channels as unknown[]).map(String)
          : ['whatsapp'];
        const labels = channels.map((c) => (c === 'whatsapp' ? 'WhatsApp' : 'correo')).join(' y ');
        e.estado = 'Docs Pendientes';
        e.history = [
          { ts: stamp(), actor: me.fullName, role: 'Asistente IA', action: `solicitó documentos por ${labels}` },
          ...e.history,
        ];
        const reqId = `REQ-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
        return delay(
          execResult(
            tool,
            { id: reqId, expedienteId: id, channels, status: 'sent' },
            { type: 'document_request', id: reqId }
          )
        );
      }
      case 'update_alert': {
        const id = asStr(input.alertId) ?? asStr(input.id) ?? '';
        const a = alerts.find((x) => x.id === id);
        if (!a) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Alerta no encontrada'));
        const estado = asStr(input.estado);
        const nota = asStr(input.nota) ?? asStr(input.resolutionNote);
        const assignedTo = asStr(input.assignedTo);
        if (estado) a.estado = estado;
        if (nota) a.notaResolucion = nota;
        if (assignedTo) a.asignada = assignedTo;
        if (estado === 'Resuelta' || estado === 'Descartada') a.resuelta = nowIso();
        alerts = alerts.map((x) => (x.id === id ? a : x));
        return delay(execResult(tool, { ...a }, { type: 'alert', id: a.id }));
      }
      case 'run_validation': {
        const id = asStr(input.expedienteId) ?? asStr(input.subjectId) ?? asStr(input.id) ?? '';
        const e = expedientes.find((x) => x.id === id || x.docId === id);
        if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
        const key = asStr(input.key) ?? 'sanctions';
        const run = runSanctions(key, {
          subjectType: e.persona,
          subjectId: e.docId,
          fullName: asStr(input.fullName) ?? e.name,
        });
        return delay(execResult(tool, run, { type: 'validation_run', id: run.id }));
      }
      case 'create_review': {
        const id = asStr(input.expedienteId) ?? asStr(input.id) ?? '';
        const e = expedientes.find((x) => x.id === id);
        if (!e) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Expediente no encontrado'));
        const r: Review = {
          id: richId('rev'),
          expedienteId: id,
          reviewType: (asStr(input.reviewType) as ReviewType) ?? 'periodica',
          riskLevel: asStr(input.riskLevel) ?? e.riesgo,
          cadenceMonths: typeof input.cadenceMonths === 'number' ? input.cadenceMonths : 12,
          scheduledFor: asStr(input.scheduledFor) ?? new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString(),
          status: 'pendiente',
          assignedTo: asStr(input.assignedTo) ?? e.analyst,
          completedAt: null,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        };
        reviewsStore[id] = [r, ...(reviewsStore[id] ?? [])];
        e.history = [
          { ts: stamp(), actor: me.fullName, role: 'Asistente IA', action: 'programó una revisión periódica' },
          ...e.history,
        ];
        return delay(execResult(tool, { ...r }, { type: 'scheduled_review', id: r.id }));
      }
      default:
        return Promise.reject(
          new RequestError(400, 'UNKNOWN_TOOL', `Herramienta no reconocida o no ejecutable: ${tool}`)
        );
    }
  },

  // --- AI assistant: the browsable capability catalog (explore cards + "/" menu) ---
  // Demo has no RBAC, so it returns the full 14-tool catalog; the real endpoint
  // filters per caller. The UI renders exactly what comes back either way.
  getAssistantTools: (): Promise<AssistantToolsResponse> =>
    delay({ tools: DEMO_ASSISTANT_TOOLS.map((t) => ({ ...t })) }),

  onboardingTrend: () =>
    delay({
      points: [2, 3, 1, 4, 3, 2, 4, 5],
      labels: ['03 may', '10 may', '17 may', '24 may', '31 may', '07 jun', '14 jun', '21 jun'],
    }),

  // --- Risk matrix (configurable factor weights + nested sub-factors) ---
  getRiskMatrix: () => delay({ ...demoRiskMatrix }),
  saveRiskMatrix: (body: RiskMatrixConfig) => {
    demoRiskMatrix = {
      version: demoRiskMatrix.version + 1,
      weights: { ...body.weights },
      thresholds: { ...body.thresholds },
      subweights: body.subweights ?? RECOMMENDED_RISK_MATRIX.subweights!,
      // La plantilla de indicadores (bandas + Usar/No usar) viaja y versiona con la matriz.
      ...(body.template ? { template: body.template } : {}),
      updatedAt: nowIso(),
    };
    saveDemo(DEMO_RISK_MATRIX_KEY, demoRiskMatrix);
    return delay({ ...demoRiskMatrix });
  },

  // --- Tenant settings (§2: firm mode) ---
  getTenantSettings: (): Promise<TenantSettingsResponse> => delay({ entityMode: demoEntityMode }),
  updateTenantSettings: (body: { entityMode: EntityMode }): Promise<TenantSettingsResponse> => {
    demoEntityMode = body.entityMode;
    saveDemo(DEMO_ENTITY_MODE_KEY, demoEntityMode);
    return delay({ entityMode: demoEntityMode });
  },

  // --- Alerts ---
  listAlerts: () => delay(alerts.map((a) => ({ ...a }))),

  getAlert: (id: string): Promise<Alert> => {
    const a = alerts.find((x) => x.id === id);
    if (!a) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Alerta no encontrada'));
    return delay({ ...a });
  },

  updateAlert: (id: string, patch: AlertPatch): Promise<Alert> => {
    const a = alerts.find((x) => x.id === id);
    if (!a) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Alerta no encontrada'));
    const beforeEstado = a.estado;
    if (patch.estado !== undefined) a.estado = patch.estado;
    if (patch.priority !== undefined) a.prioridad = patch.priority;
    if (patch.assignedTo !== undefined) a.asignada = patch.assignedTo;
    if (patch.resolutionCode !== undefined) a.codigoResolucion = patch.resolutionCode;
    if (patch.resolutionNote !== undefined) a.notaResolucion = patch.resolutionNote;
    if (patch.estado === 'Resuelta' || patch.resolutionCode) a.resuelta = nowIso();
    alerts = alerts.map((x) => (x.id === id ? a : x));
    pushAudit({
      action: 'alert.update',
      entityType: 'alert',
      entityId: a.id,
      beforeState: { estado: beforeEstado },
      afterState: { estado: a.estado },
    });
    return delay({ ...a });
  },

  // --- Renewals (§3 debida-diligencia renewal alarms) ---
  listDueRenewals: (windowDays = 0): Promise<DueRenewalResponse[]> =>
    delay(computeDueRenewals(windowDays)),

  // Escanea las renovaciones vencidas (dentro de la ventana por defecto de 30 d)
  // y abre una alerta de renovación (tipo = ALERT_TYPE_RENOVACION, prioridad por
  // nivel) por cada expediente due, en la bandeja de Alertas y Casos. Idempotente:
  // deduplica por expediente + fecha de renovación para no repetir la alarma.
  runRenewals: (): Promise<RunRenewalsResponse> => {
    const due = computeDueRenewals(30);
    const nowIsoStr = nowIso();
    const alertIds: string[] = [];
    for (const r of due) {
      const key = `${r.expedienteId}:${r.nextReviewAt}`;
      if (renewalEmitted.has(key)) continue;
      renewalEmitted.add(key);
      const nivel = (r.nivel ?? 'medio') as 'bajo' | 'medio' | 'alto';
      const prioridad = RENOVACION_PRIORITY_BY_NIVEL[nivel];
      const vencida = r.dueInDays <= 0;
      const alertId = `ALT-REN${String(renewalAlertSeq++).padStart(3, '0')}`;
      const alert: Alert = {
        id: alertId,
        tipo: ALERT_TYPE_RENOVACION,
        cliente: r.nombre,
        expedienteId: r.expedienteId,
        prioridad,
        estado: 'Generada',
        asignada: null,
        descripcion: vencida
          ? `Renovación de debida diligencia vencida (nivel ${nivel}, cada ${r.cadenceMonths} meses).`
          : `Renovación de debida diligencia próxima en ${r.dueInDays} día(s) (nivel ${nivel}, cada ${r.cadenceMonths} meses).`,
        evidencia: [
          { label: 'Expediente', value: r.expedienteRef },
          { label: 'Nivel de riesgo', value: nivel },
          { label: 'Cadencia', value: `${r.cadenceMonths} meses` },
          { label: 'Próxima revisión', value: r.nextReviewAt ?? '—' },
          { label: 'Origen', value: 'Renovación de debida diligencia · escaneo de vencimientos' },
        ],
        abierta: nowIsoStr,
      };
      alerts = [alert, ...alerts];
      alertIds.push(alertId);
    }
    return delay({ revisados: due.length, nuevasAlertas: alertIds.length, alertIds }, 600);
  },

  // --- Subjects ---
  listSubjects: (expedienteId: string): Promise<Subject[]> =>
    delay((subjectsStore[expedienteId] ?? []).map((s) => ({ ...s }))),

  createSubject: (expedienteId: string, body: CreateSubjectInput): Promise<Subject> => {
    const s: Subject = {
      id: richId('sub'),
      expedienteId,
      subjectRef: body.subjectRef,
      subjectType: body.subjectType,
      declaredAddress: body.declaredAddress ?? null,
      sector: body.sector ?? null,
      activity: body.activity ?? null,
      requiredDocuments: body.requiredDocuments ?? [],
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    subjectsStore[expedienteId] = [s, ...(subjectsStore[expedienteId] ?? [])];
    return delay({ ...s });
  },

  getSubject: (expedienteId: string, subjectId: string): Promise<Subject> => {
    const s = (subjectsStore[expedienteId] ?? []).find((x) => x.id === subjectId);
    if (!s) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Sujeto no encontrado'));
    return delay({ ...s });
  },

  updateSubject: (
    expedienteId: string,
    subjectId: string,
    patch: SubjectPatch
  ): Promise<Subject> => {
    const s = (subjectsStore[expedienteId] ?? []).find((x) => x.id === subjectId);
    if (!s) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Sujeto no encontrado'));
    if (patch.subjectType !== undefined) s.subjectType = patch.subjectType;
    if (patch.declaredAddress !== undefined) s.declaredAddress = patch.declaredAddress;
    if (patch.sector !== undefined) s.sector = patch.sector;
    if (patch.activity !== undefined) s.activity = patch.activity;
    if (patch.requiredDocuments !== undefined) s.requiredDocuments = patch.requiredDocuments;
    s.updatedAt = nowIso();
    return delay({ ...s });
  },

  // --- Owners (UBO graph) ---
  listOwners: (expedienteId: string): Promise<Owner[]> =>
    delay((ownersStore[expedienteId] ?? []).map((o) => ({ ...o }))),

  createOwner: (expedienteId: string, body: CreateOwnerInput): Promise<Owner> => {
    // Auto-create parity: al registrar un beneficiario final persona-natural sin
    // kycExpedienteId, el backend crea su expediente KYC y lo vincula. Los owners
    // jurídicos intermedios NO generan expediente (solo se enlazan si viene uno).
    let kycExpedienteId = body.kycExpedienteId ?? null;
    if (body.ownerType === 'natural' && !kycExpedienteId) {
      kycExpedienteId = spawnNaturalExpediente(body.name, body.idNumber ?? '—');
    }
    const o: Owner = {
      id: richId('own'),
      expedienteId,
      subjectRef: body.subjectRef,
      parentOwnerId: body.parentOwnerId ?? null,
      name: body.name,
      ownerType: body.ownerType,
      ownershipPct: body.ownershipPct,
      idNumber: body.idNumber ?? null,
      country: body.country ?? null,
      kycComplete: body.kycComplete ?? false,
      kycExpedienteId,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    ownersStore[expedienteId] = [o, ...(ownersStore[expedienteId] ?? [])];
    return delay({ ...o });
  },

  getOwner: (expedienteId: string, ownerId: string): Promise<Owner> => {
    const o = (ownersStore[expedienteId] ?? []).find((x) => x.id === ownerId);
    if (!o) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Beneficiario no encontrado'));
    return delay({ ...o });
  },

  updateOwner: (expedienteId: string, ownerId: string, patch: OwnerPatch): Promise<Owner> => {
    const o = (ownersStore[expedienteId] ?? []).find((x) => x.id === ownerId);
    if (!o) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Beneficiario no encontrado'));
    if (patch.name !== undefined) o.name = patch.name;
    if (patch.ownerType !== undefined) o.ownerType = patch.ownerType;
    if (patch.ownershipPct !== undefined) o.ownershipPct = patch.ownershipPct;
    if (patch.parentOwnerId !== undefined) o.parentOwnerId = patch.parentOwnerId;
    if (patch.idNumber !== undefined) o.idNumber = patch.idNumber;
    if (patch.country !== undefined) o.country = patch.country;
    if (patch.kycComplete !== undefined) o.kycComplete = patch.kycComplete;
    if (patch.kycExpedienteId !== undefined) o.kycExpedienteId = patch.kycExpedienteId;
    o.updatedAt = nowIso();
    return delay({ ...o });
  },

  // --- Directivos del Registro Público (sincronizados por el barrido; el demo
  // no simula el barrido RP, así que siempre está vacío — el panel muestra el
  // estado vacío indicando que hace falta ejecutar la consulta al Registro Público).
  listRegistryOfficers: (_expedienteId: string): Promise<RegistryOfficerResponse[]> => delay([]),

  // --- Vínculos (reverse ownership) ---
  // Recorre todos los owners en memoria y devuelve las entidades (expedientes
  // padre) donde este expediente figura como beneficiario final (kycExpedienteId).
  getVinculos: (expedienteId: string): Promise<VinculoResponse[]> => {
    const out: VinculoResponse[] = [];
    for (const [parentExpId, list] of Object.entries(ownersStore)) {
      for (const o of list) {
        if (o.kycExpedienteId === expedienteId) {
          const parent = expedientes.find((e) => e.id === parentExpId);
          out.push({
            ownerId: o.id,
            expedienteId: parentExpId,
            entityName: parent?.name ?? parentExpId,
            entityKind: parent?.kind ?? 'Persona jurídica',
            ownershipPct: o.ownershipPct,
          });
        }
      }
    }
    return delay(out);
  },

  // --- Reviews (periodic re-review) ---
  listReviews: (expedienteId: string): Promise<Review[]> =>
    delay((reviewsStore[expedienteId] ?? []).map((r) => ({ ...r }))),

  createReview: (expedienteId: string, body: CreateReviewInput): Promise<Review> => {
    const r: Review = {
      id: richId('rev'),
      expedienteId,
      reviewType: body.reviewType ?? 'periodica',
      riskLevel: body.riskLevel ?? null,
      cadenceMonths: body.cadenceMonths ?? null,
      scheduledFor: body.scheduledFor,
      status: 'pendiente',
      assignedTo: body.assignedTo ?? null,
      completedAt: null,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    reviewsStore[expedienteId] = [r, ...(reviewsStore[expedienteId] ?? [])];
    return delay({ ...r });
  },

  getReview: (expedienteId: string, reviewId: string): Promise<Review> => {
    const r = (reviewsStore[expedienteId] ?? []).find((x) => x.id === reviewId);
    if (!r) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Revisión no encontrada'));
    return delay({ ...r });
  },

  updateReview: (
    expedienteId: string,
    reviewId: string,
    patch: ReviewPatch
  ): Promise<Review> => {
    const r = (reviewsStore[expedienteId] ?? []).find((x) => x.id === reviewId);
    if (!r) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Revisión no encontrada'));
    if (patch.scheduledFor !== undefined) r.scheduledFor = patch.scheduledFor;
    if (patch.reviewType !== undefined) r.reviewType = patch.reviewType;
    if (patch.riskLevel !== undefined) r.riskLevel = patch.riskLevel;
    if (patch.cadenceMonths !== undefined) r.cadenceMonths = patch.cadenceMonths;
    if (patch.status !== undefined) {
      r.status = patch.status;
      r.completedAt = patch.status === 'completada' ? nowIso() : r.completedAt;
    }
    if (patch.assignedTo !== undefined) r.assignedTo = patch.assignedTo;
    r.updatedAt = nowIso();
    return delay({ ...r });
  },

  // --- Credit consents ---
  listConsents: (expedienteId: string): Promise<Consent[]> =>
    delay((consentsStore[expedienteId] ?? []).map((c) => ({ ...c }))),

  createConsent: (expedienteId: string, body: CreateConsentInput): Promise<Consent> => {
    const c: Consent = {
      id: richId('con'),
      expedienteId,
      subjectRef: body.subjectRef,
      subjectType: body.subjectType,
      source: body.source,
      evidenceRef: body.evidenceRef ?? null,
      grantedAt: body.grantedAt ?? nowIso(),
      expiresAt: body.expiresAt ?? null,
      revokedAt: null,
      recordedBy: me.fullName,
      createdAt: nowIso(),
    };
    consentsStore[expedienteId] = [c, ...(consentsStore[expedienteId] ?? [])];
    return delay({ ...c });
  },

  getConsent: (expedienteId: string, consentId: string): Promise<Consent> => {
    const c = (consentsStore[expedienteId] ?? []).find((x) => x.id === consentId);
    if (!c) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Consentimiento no encontrado'));
    return delay({ ...c });
  },

  updateConsent: (
    expedienteId: string,
    consentId: string,
    patch: ConsentPatch
  ): Promise<Consent> => {
    const c = (consentsStore[expedienteId] ?? []).find((x) => x.id === consentId);
    if (!c) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Consentimiento no encontrado'));
    if (patch.source !== undefined) c.source = patch.source;
    if (patch.evidenceRef !== undefined) c.evidenceRef = patch.evidenceRef;
    if (patch.expiresAt !== undefined) c.expiresAt = patch.expiresAt;
    if (patch.revokedAt !== undefined) c.revokedAt = patch.revokedAt;
    return delay({ ...c });
  },

  // --- Alcance a clientes (outreach: suggested/scheduled/sent messages) ---
  generateOutreach: (): Promise<OutreachListResponse> => {
    for (const c of OUTREACH_CANDIDATES) {
      const exists = outreachMessages.some(
        (m) => m.expedienteId === c.expedienteId && m.reason === c.reason && m.status !== 'canceled'
      );
      if (exists) continue;
      outreachMessages = [buildOutreachMessage(c), ...outreachMessages];
    }
    return delay({ items: outreachMessages.map((m) => ({ ...m })), total: outreachMessages.length }, 500);
  },

  listOutreach: (status?: string): Promise<OutreachListResponse> => {
    const items = status ? outreachMessages.filter((m) => m.status === status) : outreachMessages;
    return delay({ items: items.map((m) => ({ ...m })), total: items.length });
  },

  updateOutreach: (id: string, body: UpdateOutreachInput): Promise<OutreachMessageResponse> => {
    const m = outreachMessages.find((x) => x.id === id);
    if (!m) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Mensaje no encontrado'));
    if (body.subject !== undefined) m.subject = body.subject;
    if (body.body !== undefined) m.body = body.body;
    if (body.channels !== undefined) m.channels = body.channels;
    if (body.scheduledAt !== undefined) {
      m.scheduledAt = body.scheduledAt;
      m.status = body.scheduledAt ? 'scheduled' : 'draft';
    }
    m.updatedAt = nowIso();
    return delay({ ...m });
  },

  sendOutreach: (id: string): Promise<OutreachMessageResponse> => {
    const m = outreachMessages.find((x) => x.id === id);
    if (!m) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Mensaje no encontrado'));
    m.status = 'sent';
    m.sentAt = nowIso();
    m.error = null;
    m.updatedAt = nowIso();
    return delay({ ...m }, 500);
  },

  cancelOutreach: (id: string): Promise<OutreachMessageResponse> => {
    const m = outreachMessages.find((x) => x.id === id);
    if (!m) return Promise.reject(new RequestError(404, 'NOT_FOUND', 'Mensaje no encontrado'));
    m.status = 'canceled';
    m.updatedAt = nowIso();
    return delay({ ...m });
  },

  runOutreach: (): Promise<RunOutreachResponse> => {
    const scheduled = outreachMessages.filter((m) => m.status === 'scheduled');
    for (const m of scheduled) {
      m.status = 'sent';
      m.sentAt = nowIso();
      m.updatedAt = nowIso();
    }
    return delay({ revisados: scheduled.length, enviados: scheduled.length, fallidos: 0 }, 600);
  },

  // --- Audit trail (append-only compliance log) ---
  listAudit: (
    params: {
      limit?: number;
      cursor?: string;
      entityType?: string;
      action?: string;
      from?: string;
      to?: string;
    } = {}
  ): Promise<AuditListResponse> => {
    let items = auditLog.map((e) => ({ ...e }));
    if (params.entityType) items = items.filter((e) => e.entityType === params.entityType);
    if (params.action) items = items.filter((e) => e.action === params.action);
    if (params.from) items = items.filter((e) => e.createdAt >= (params.from as string));
    if (params.to) items = items.filter((e) => e.createdAt <= (params.to as string));
    // The demo returns a single page (no real cursor paging over the seed rows).
    return delay({ items, nextCursor: null });
  },

  // CSV (default) or PDF (`format: 'pdf'`) export, mirroring the real endpoint's
  // filters, built client-side (no network — I6) over the same LIVE audit log
  // the list view reads from (seed + appended events).
  downloadAuditReport: (
    params: {
      entityType?: string;
      action?: string;
      from?: string;
      to?: string;
      format?: 'csv' | 'pdf';
    } = {}
  ): Promise<Blob> => {
    let items = auditLog.map((e) => ({ ...e }));
    if (params.entityType) items = items.filter((e) => e.entityType === params.entityType);
    if (params.action) items = items.filter((e) => e.action === params.action);
    if (params.from) items = items.filter((e) => e.createdAt >= (params.from as string));
    if (params.to) items = items.filter((e) => e.createdAt <= (params.to as string));
    if (params.format === 'pdf') {
      const lines = items
        .slice(0, 200)
        .map((e) => `${e.createdAt}  ${auditActionLabel(e.action)}  ${e.entityType}/${e.entityId}  ${e.actorName}`);
      return delay(buildSimplePdf('Auditoría — Cumpli (demostración)', lines));
    }
    const header = 'fecha,accion,tipoEntidad,idEntidad,actor,rol\n';
    const rows = items
      .map((e) =>
        [e.createdAt, e.action, e.entityType, e.entityId, e.actorName, e.actorRole]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');
    return delay(new Blob([header + rows], { type: 'text/csv;charset=utf-8' }));
  },

  getAuditEvidenceUrl: (eventKey: string): Promise<DocumentDownloadUrlResponse> => {
    const ev = auditLog.find((e) => e.eventKey === eventKey);
    const heading = ev?.evidenceName ?? 'Evidencia de cumplimiento';
    return delay(
      svgDataUri(heading, [
        `Acción: ${ev?.action ?? 'validation.run'}`,
        `Entidad: ${ev?.entityType ?? 'validation'} · ${ev?.entityId ?? '—'}`,
        `Responsable: ${ev?.actorName ?? me.fullName}`,
        `Fecha: ${ev?.createdAt ?? nowIso()}`,
        '',
        'Cotejo contra listas: OFAC · ONU · UE · Reino Unido · Canadá.',
        'Registro append-only conforme al artículo de conservación de la Ley 23.',
      ]),
      400
    );
  },
};

